/* Maatcoach: personal size advice on every product page. Loaded by snippets/maatcoach.liquid, no dependencies.

   The shopper answers once, in steps (since 27-10-2026): height and weight on two rulers she swipes, her build (or her
   own measurements), how she likes her clothes to fit; for shoes her usual size on a ruler. The answers stay on this
   device (localStorage under a device id, with a profile per person; nothing is sent
   anywhere), and from then on every product page names her size under the sizes and marks it, with the advice in the
   size guide pop-up. The profile icon in the head of the pop-up shows every answer, to change one by one.

   Two halves in this file:
   - the engine: pure functions, no DOM. It reads the product's size chart (custom.size_chart: columns plus rows),
     repairs what supplier charts get wrong and advises one size. scripts/shopify/maatcoach.mjs runs this same code in
     Node over every product of a store, so the check sees exactly what the shopper gets;
   - the page: the steps, the rulers, her profile and the advice inside the pop-up, and the button under the sizes.

   How the advice is made (sources and formulas: google-stores/store-15/research/taglie-en-maatadvies-2026-09-27.md):
   1. Her body. Hips from height and weight (Hip Index exponents, 103 cm at 73 kg and 166 cm), waist and bust from the
      hips along the German size table; the build moves all three. Her own measurements replace the estimate. The
      same model as the jeans advice in assets/size-guide.js.
   2. What this model intends. For every size of the chart: garment measurement minus the standard body of that size
      label. The median is the ease the model is cut with: a wrap coat cut oversize has +43 cm at the bust, a ribbed
      turtleneck -18 cm. She gets the size where her body plus that same ease meets the chart, so an oversize coat
      still comes out oversize on her and a stretch knit still hugs.
   3. One size, always. Between two sizes the kind of garment decides (the larger, for a jumper underneath or more
      room; an ample cut the smaller) and the other size is offered next to it. Her fit preference, when she gave one,
      moves her a quarter of a size and decides between two sizes instead (FIT).
   Shoes: her usual size, one up when this model's foot length runs half a size short, never one down on the chart's
   word alone; with her foot length, the smallest size whose foot length takes it. Sizes in EU or in US women's sizes
   (thema 2: the form asks in US sizes in the United States and Canada).

   Repairs, each reported by the check script: waist and hips swapped (a trouser waist wider than its hips), flat
   widths instead of circumferences, measurements that shrink as the size grows, a foot column that is really an
   outsole. A column that cannot be trusted is dropped; with no column left, the advice falls back to her usual size
   on the standard table, and says so. A chart with only the supplier's weight per size is read as the body of each
   size (fromWeight). Which kind of garment it is comes from the product type first, the title second (kindOf). */
(function (root) {
  'use strict';
  /* Loaded once: the header ("Ma taille" in the menu) and the product page both ask for this file. */
  if (root.Maatcoach && root.Maatcoach.loaded) return;

  /* ======================================================================================================== */
  /* Engine                                                                                                   */
  /* ======================================================================================================== */
  var MC = { version: 1, loaded: true };

  /* Standard body per size label, cm: bust, waist, hips. XXS to 5XL are the German sizes 32 to 50: waist and hips
     are the midpoints of the German size table, the bust is 2 x (size + 6). */
  var LETTERS = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL', '5XL'];
  var STD = [
    [76, 59.5, 83], [80, 63, 87], [84, 66.5, 92], [88, 70.5, 96], [92, 75, 99.5],
    [96, 79.5, 103], [100, 83.5, 106.5], [104, 88, 110.5], [108, 93, 114.5], [112, 99, 119.5]
  ];
  var AXIS = { chest: 0, waist: 1, hip: 2 };

  /* Bust 1.067 x hips - 14.1 is the German table read the same way (bust against hips, within 1.5 cm from size 34 to
     48). The build moves bust, waist and hips by the same centimetres, as in the jeans advice. */
  var BODY = {
    hip: function (h, w) { return 103 * Math.pow(w / 73, 0.482) * Math.pow(h / 166, -0.31); },
    waist: function (hip) { return 1.1 * hip - 33.5; },
    chest: function (hip) { return 1.067 * hip - 14.1; },
    build: { slim: -2, mid: 0, full: 2.5 }
  };

  /* Men (thema 2, 08-10-2026, owner: "bij de mannenstore ... dat hier de postuur staat van een man").
     Standard body per letter from the German men's sizes 44 to 62 (chest = 2 x size; S = 46, M = 48/50, L = 52/54,
     XL = 56/58, XXL = 60/62, waist and hips from the same table). His body: chest from height and weight by geometric
     scaling (a girth grows with the square root of weight over height), 100 cm at 80 kg and 178 cm; waist and hips
     follow the chest along the same table. The build moves all three, as for women. */
  var STD_MEN = [
    [84, 72, 88], [88, 76, 92], [92, 80, 96], [98, 86, 100], [106, 94, 106],
    [114, 102, 112], [122, 110, 118], [128, 117, 124], [134, 124, 130], [140, 131, 136]
  ];
  var BODY_MEN = {
    chest: function (h, w) { return 100 * Math.pow(w / 80, 0.5) * Math.pow(h / 178, -0.5); },
    waist: function (chest) { return 1.25 * chest - 39; },
    hip: function (chest) { return 0.6 * chest + 42; },
    build: { slim: -2.5, mid: 0, full: 3 }
  };
  var STD_WOMEN = STD;
  MC.kind = 'women';
  /* Which body the advice uses: 'men' or 'women' (the default). The page sets it from data-mc-body. */
  MC.setBody = function (kind) {
    MC.kind = kind === 'men' ? 'men' : 'women';
    STD = MC.kind === 'men' ? STD_MEN : STD_WOMEN;
    /* US shoe sizes follow the body: men's sizes for men (SHOE_US_MEN below), women's otherwise. */
    SHOE.us = MC.kind === 'men' ? SHOE_US_MEN : SHOE_US_WOMEN;
  };

  /* A design ease outside these bounds is a broken chart, not a design. When the size labels say nothing about the
     body (no letters), the rule-of-thumb ease below stands in and the check script says so. */
  var EASE_RANGE = { chest: [-25, 50], waist: [-20, 60], hip: [-25, 45] };
  var EASE_DEFAULT = { chest: 12, waist: 4, hip: 6 };

  /* How she likes her clothes to fit (step 3 of the coach, owner 27-10-2026): close-fitting or relaxed moves her a
     quarter of a size step and decides between two sizes. A relaxed cut is already roomy and a close-fitting cut already
     hugs, so there the preference keeps the design. Without an answer: regular, the advice as before. */
  var FIT = { near: -0.25, regular: 0, loose: 0.25 };

  /* Her figure per zone (step 2, owner 27-10-2026: "silhouet per zone"): bust, stomach and hips each smaller, average or
     fuller, in centimetres on that zone only (about one standard deviation for a given height and weight). Her answers
     are zc, zw and zh; answers from before, one build for the whole body (b), still count when those are missing. */
  var ZONE = { chest: { less: -3.5, more: 4 }, waist: { less: -3.5, more: 5 }, hip: { less: -3.5, more: 4 } };
  var ZONE_KEY = { chest: 'zc', waist: 'zw', hip: 'zh' };

  /* A correction per model from exchanges (owner 27-10-2026: "leren van ruilingen"): the product metafield
     custom.maatcoach_shift, in size steps, written by scripts/shopify/maatcoach-ruilingen.mjs from returns with the reason
     too small or too large. +0.25 means this model runs a quarter size small, so she moves up. Bounded, so one bad week
     cannot move the advice by more than half a size. */
  var SHIFT_MAX = 0.5;

  /* Shoe sizes in two systems: EU, in whole sizes, and US women's, in half sizes
     ("US 6-6.5", "US7"; thema 2, 03-10-2026, for the American stores). Per system the foot length most of our shoe
     charts give: EU 35: 22.5 cm, 38: 24 cm, 40: 25 cm; US 6: 23 cm, 8: 24.6 cm, 11: 27 cm (measured on the US charts of
     our earlier shops). A model whose own column sits 0.4 cm or more below it at her size runs small. The same
     foot length carries her size over to the other system when she answered in that one. her: the usual sizes the
     form accepts. */
  var SHOE = {
    eu: { lo: 30, hi: 50, her: [30, 48], whole: true, foot: function (x) { return 22.5 + 0.5 * (x - 35); }, size: function (f) { return 35 + (f - 22.5) / 0.5; } },
    us: { lo: 3, hi: 14, her: [3, 14], whole: false, foot: function (x) { return 23 + 0.8 * (x - 6); }, size: function (f) { return 6 + (f - 23) / 0.8; } }
  };
  /* US men's sizes (a men's shop, maatcoach_men): the usual retail table puts EU one step of 33 above US (US 8 = EU 41,
     US 9 = EU 42, US 10 = EU 43), so on the EU scale above a US size is 0.5 cm of foot per size. MC.setBody swaps it in
     for the men's body; the women's table stays the default. */
  var SHOE_US_WOMEN = SHOE.us;
  var SHOE_US_MEN = { lo: 5, hi: 16, her: [5, 16], whole: false, foot: function (x) { return 21.5 + 0.5 * x; }, size: function (f) { return (f - 21.5) / 0.5; } };
  /* A men's shop says so in window.theme (layout/theme.liquid, mcMen), so the card advice on collection pages, which loads
     this script without a coach box, also uses the men's body. */
  if (window.theme && window.theme.mcMen) MC.setBody('men');
  function shoeConvert(v, from, to) {
    if (from === to) return v;
    return Math.round(SHOE[to].size(SHOE[from].foot(v)) * 2) / 2;
  }

  /* Words, in the store languages we sell in: French, English, Dutch, German, Italian, Polish, Danish, Spanish. */
  var RX = {
    one: /taille unique|one ?size|onesize|one-size|taglia unica|einheitsgr|uniwersaln|unica|universal|\bunique\b|one fits/i,
    shoe: /chaussure|botte|bottine|basket|sandale|escarpin|mocassin|chausson|pantoufle|\bmules?\b|sneaker|\bboots?\b|\bshoes?\b|sandal|slipper|loafer|\bpumps?\b|schoen|laars|laarzen|pantoffel|slof|schuh|stiefel|scarpe|scarpa|stival|pantofol|\bbuty\b|kozaki|botki|kapcie|\bsko\b|støvle|tøffel|zapato|\bbotas?\b|zapatilla/,
    set: /ensemble|\bsets?\b|co-?ord|pyjama|pajama|twin-?set|completo|zestaw|\bsæt\b|conjunto|tailleur/,
    bottom: /pantalon|legging|jogging|jogger|\bjeans?\b|jupe|\bshorts?\b|bermuda|trouser|\bpants\b|skirt|broek|\brok\b|\bhose\b|\brock\b|pantaloni|gonna|spodnie|spódnic|bukser|nederdel|falda|culotte/,
    dress: /\brobe|dress|jurk|kleid|abito|vestito|sukienk|kjole|vestido|combinaison|jumpsuit|overall|salopette/,
    top: /chemise|blouse|\bhauts?\b|\btops?\b|t-shirt|tee-shirt|tunique|débardeur|\bshirts?\b|overhemd|bloes|\bhemd|bluse|oberteil|camicia|camicett|koszul|bluzk|skjorte|camisa|blusa/,
    outer: /manteau|veste|doudoune|parka|blouson|\bcape\b|trench|caban|\bcoat|jacket|\bjas\b|\bmantel|jacke|giacca|cappotto|piumino|kurtka|płaszcz|frakke|jakke|abrigo|chaqueta|anorak|bodywarmer/,
    knit: /\bpull|gilet|cardigan|sweat|maille|tricot|\btrui|\bvest\b|pullover|strick|maglion|maglia|sweter|\bstrik|jersey|jumper|\bknit/,
    ample: /oversize|\bample|cocooning|chauve-souris|batwing|\bloose|ruimvallend|weit geschnitten|ampio|luźn|\bløs/,
    wide: /large|wide|ample|palazzo|évasé|evase|flare|baggy|jogging|jogger|wijd|weit|ampi|szerok|\bvid/,
    narrow: /droit|straight|slim|legging|skinny|cigarette|recht|gerade|dritt|prost|\blige/
  };

  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    var s = String(v == null ? '' : v).replace(/,/g, '.');
    var m = s.match(/\d+(?:\.\d+)?/g);
    if (!m) return null;
    var a = parseFloat(m[0]);
    /* "100 to 104", "100-104": the middle. */
    if (m.length > 1 && /\d\s*(?:-|\u2013|\u2014|~|to|à|bis|tot|do|til)\s*\d/i.test(s)) return (a + parseFloat(m[1])) / 2;
    return a;
  }

  function median(list) {
    if (!list.length) return null;
    var s = list.slice().sort(function (x, y) { return x - y; });
    var m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
  }

  function sizeKey(s) {
    var k = String(s == null ? '' : s).toUpperCase().replace(/\s+/g, '');
    if (k === '2XL') return 'XXL';
    if (k === 'XXXL') return '3XL';
    if (k === 'XXXXL') return '4XL';
    if (k === 'XXXXXL') return '5XL';
    return k;
  }

  function letterIndex(s) {
    var k = sizeKey(s);
    var i = LETTERS.indexOf(k);
    if (i > -1) return i;
    if (/^\d{2}$/.test(k)) {
      var n = parseInt(k, 10);
      if (n >= 32 && n <= 50 && n % 2 === 0) return (n - 32) / 2;
    }
    return -1;
  }

  /* The standard body for a size label; "S/M" sits halfway. Null for labels without a body (a shoe size, "One size"). */
  function stdOf(size) {
    var parts = String(size).split('/');
    var sum = 0;
    for (var i = 0; i < parts.length; i++) {
      var li = letterIndex(parts[i]);
      if (li < 0) return null;
      sum += li;
    }
    var x = sum / parts.length;
    var lo = Math.floor(x);
    var hi = Math.ceil(x);
    var f = x - lo;
    return [0, 1, 2].map(function (a) { return STD[lo][a] + f * (STD[hi][a] - STD[lo][a]); });
  }

  function shoeRange(s) {
    var t = String(s).replace(/,/g, '.');
    var sys = 'us';
    var m = t.match(/\bUS\s*(\d{1,2}(?:\.5)?)(?:\s*[-\u2013\/]\s*(?:US\s*)?(\d{1,2}(?:\.5)?))?/i);
    if (!m) {
      sys = 'eu';
      m = t.match(/(\d{2}(?:\.5)?)(?:\s*[-\u2013\/]\s*(\d{2}(?:\.5)?))?/);
    }
    if (!m) return null;
    var lo = parseFloat(m[1]);
    var hi = m[2] ? parseFloat(m[2]) : lo;
    if (lo < SHOE[sys].lo || hi > SHOE[sys].hi || hi < lo) return null;
    return { sys: sys, lo: lo, hi: hi, c: (lo + hi) / 2 };
  }

  /* What a chart column measures. A set prefixes its columns with the piece ("Hauts", "Pantalons"). Foot girth and
     shaft circumference are not the foot length. */
  function colDim(label) {
    var s = String(label || '').toLowerCase();
    var part = null;
    if (/^(hauts?|tops?|bovenstuk|oberteil|parte superiore|góra|overdel)\b/.test(s)) part = 'top';
    else if (/^(pantalons?|bas|bottoms?|jupes?|shorts?|broek|hose|pantaloni|gonna|spodnie|bukser|underdel|dół)\b/.test(s)) part = 'bottom';
    var dim = null;
    if (/tour de pied|girth|omtrek|umfang des fu|ballen|circonferenza del piede|obwód stopy|tour de tige|mollet|calf|kuit|wade|polpaccio|łydk|læg/.test(s)) dim = null;
    else if (/poitrine|chest|bust|borst|brust|oberweite|petto|seno|torace|klatk|biust|bryst|pecho|busto/.test(s)) dim = 'chest';
    else if (/hanche|\bhips?\b|heup|hüft|huft|fianch|bacino|biodr|hofte|cadera/.test(s)) dim = 'hip';
    else if (/tour de taille|waist|taillen|^taille|\btaille \(|\bvita\b|talia|talje|cintura|ceinture|waistband|tailleband/.test(s)) dim = 'waist';
    else if (/entrejambe|inseam|binnenbeen|innenbein|schritt|cavallo|interno|wewnętrzn|indvendig|entrepierna/.test(s)) dim = 'inseam';
    else if (/\bpied\b|\bfoot\b|voet|fuß|fuss|piede|stopa|stopy|\bfod\b|\bpie\b|semelle|insole|binnenzool|innensohle|soletta|wkładk|indersål|plantilla/.test(s)) dim = 'foot';
    else if (/poids|weight|gewicht|peso|waga|vægt|vikt/.test(s)) dim = 'weight';
    return { dim: dim, part: part, lb: dim === 'weight' && /\blbs?\b|pound/.test(s) };
  }

  function readChart(chart) {
    var out = { rows: [], measure: 'garment', cols: [] };
    if (!chart || !chart.rows || !chart.columns) return out;
    var factor = /^in/i.test(String(chart.unit || 'cm')) ? 2.54 : 1;
    out.measure = String(chart.measure || 'garment').toLowerCase() === 'body' ? 'body' : 'garment';
    for (var c = 0; c < chart.columns.length; c++) out.cols.push(colDim(chart.columns[c]));
    for (var r = 0; r < chart.rows.length; r++) {
      var row = chart.rows[r];
      if (!row || row.size == null) continue;
      var v = {};
      var cells = row.values || [];
      for (var k = 0; k < out.cols.length; k++) {
        var cd = out.cols[k];
        if (!cd.dim || v[cd.dim] != null) continue;
        if (cd.part === 'bottom' && cd.dim === 'chest') continue;
        if (cd.part === 'top' && (cd.dim === 'waist' || cd.dim === 'hip' || cd.dim === 'inseam')) continue;
        var n = num(cells[k]);
        if (n == null || n <= 0) continue;
        if (cd.dim === 'weight') {
          /* The supplier's range itself, "47 à 55": the advice keeps to its edges (fromWeight). */
          var kg = cd.lb ? 0.45359237 : 1;
          var all = String(cells[k]).replace(/,/g, '.').match(/\d+(?:\.\d+)?/g) || [];
          var range = all.length > 1 && /\d\s*(?:-|\u2013|\u2014|~|to|à|bis|tot|do|til)\s*\d/i.test(String(cells[k]));
          v.weight = n * kg;
          v.wlo = (range ? parseFloat(all[0]) : n) * kg;
          v.whi = (range ? parseFloat(all[1]) : n) * kg;
        } else {
          v[cd.dim] = n * factor;
        }
      }
      out.rows.push({ size: String(row.size), key: sizeKey(row.size), std: stdOf(row.size), v: v });
    }
    return out;
  }

  /* Waist and hips swapped: a trouser waist is never wider than its hips, so when most rows say so the two columns
     were labelled the wrong way round (wide-leg knit trousers, 2026: "waist" 114, "hips" 71 in XXS). */
  function fixSwap(rows, notes) {
    var both = 0;
    var flipped = 0;
    rows.forEach(function (r) {
      if (r.v.waist != null && r.v.hip != null) {
        both++;
        if (r.v.waist > r.v.hip + 1) flipped++;
      }
    });
    if (both && flipped * 2 > both) {
      rows.forEach(function (r) {
        var w = r.v.waist;
        r.v.waist = r.v.hip;
        r.v.hip = w;
      });
      notes.push('swap');
    }
  }

  /* One circumference over the given rows: its values (doubled when the chart gives flat widths), the design ease and
     the grading step. Null when the column cannot be trusted. */
  function prepDim(all, rows, d, measure, notes, quiet) {
    var list = rows.filter(function (r) { return r.v[d] != null; });
    if (list.length < 2) return null;
    var raw = list.map(function (r) { return r.v[d]; });
    for (var i = 0; i < raw.length - 1; i++) {
      if (raw[i + 1] < raw[i] - 1) {
        if (!quiet) notes.push(d + ':order');
        return null;
      }
    }
    var half = measure !== 'body' && median(raw) < (d === 'waist' ? 45 : 60);
    var factor = half ? 2 : 1;
    var vals = raw.map(function (x) { return x * factor; });
    var T = 0;
    if (measure !== 'body') {
      /* A pattern is drafted at a base size and graded from there, and our suppliers grade L and up with extra room
         (+4 cm per size up to M, then +6). So the ease the model is designed with comes from the sizes up to M; the
         larger sizes keep their extra room, which the chart then shows. */
      var diffs = [];
      var core = [];
      all.forEach(function (r) {
        if (r.v[d] == null || !r.std) return;
        var e = r.v[d] * factor - r.std[AXIS[d]];
        diffs.push(e);
        if (r.std[0] <= STD[3][0]) core.push(e);
      });
      /* The factory's own weight per size, where the chart gives it for two sizes or more, says the fit it cut the model
         for, also when its letters run small: per size the body of the middle of that range at the anchor height,
         against the garment (28-10-2026: an oversized roll neck whose S is for 45 to 50 kg got S at 160 cm and
         58 kg, where the factory says L). */
      var byW = [];
      all.forEach(function (r) {
        if (r.v[d] == null || r.v.wlo == null) return;
        byW.push(r.v[d] * factor - bodyAt((r.v.wlo + r.v.whi) / 2)[d]);
      });
      if (byW.length >= 2) {
        T = median(byW);
      } else if (core.length >= 2) {
        T = median(core);
      } else if (diffs.length >= 2) {
        T = median(diffs);
      } else {
        T = EASE_DEFAULT[d];
        if (!quiet) notes.push(d + ':default');
      }
      if (T < EASE_RANGE[d][0] || T > EASE_RANGE[d][1]) {
        if (!quiet) notes.push(d + ':implausible');
        return null;
      }
      if (byW.length >= 2 && !quiet) notes.push(d + ':weight');
      if (half && !quiet) notes.push(d + ':half');
    }
    var steps = [];
    for (var j = 0; j < vals.length - 1; j++) if (vals[j + 1] - vals[j] > 0.5) steps.push(vals[j + 1] - vals[j]);
    return { dim: d, sizes: list.map(function (r) { return r.size; }), vals: vals, T: T, step: median(steps) || 4, rows: list };
  }

  function prepShoe(sell, notes) {
    var rows = [];
    var sys = null;
    sell.forEach(function (r) {
      var rg = shoeRange(r.size);
      if (!rg) return;
      /* One system per model: a label in the other one is left out, and reported. */
      if (sys && rg.sys !== sys) {
        if (notes.indexOf('shoe:mixed') < 0) notes.push('shoe:mixed');
        return;
      }
      sys = rg.sys;
      rows.push({ size: r.size, lo: rg.lo, hi: rg.hi, c: rg.c, foot: r.v.foot != null ? r.v.foot : null });
    });
    sys = sys || 'eu';
    var withFoot = rows.filter(function (x) { return x.foot != null; });
    var ok = withFoot.length >= 2;
    if (ok) {
      for (var i = 0; i < withFoot.length - 1; i++) {
        if (withFoot[i + 1].foot < withFoot[i].foot - 0.05) { ok = false; notes.push('foot:order'); break; }
      }
    }
    if (ok && withFoot.some(function (x) { return x.foot < 19 || x.foot > 32; })) { ok = false; notes.push('foot:range'); }
    var dev = ok ? median(withFoot.map(function (x) { return x.foot - SHOE[sys].foot(x.c); })) : null;
    if (ok && Math.abs(dev) > 1.5) { ok = false; notes.push('foot:scale'); }
    return { rows: rows, footOk: ok, dev: dev, sys: sys };
  }

  function kindByWords(text) {
    if (RX.shoe.test(text)) return 'shoe';
    if (RX.set.test(text)) return 'set';
    if (RX.bottom.test(text)) return 'bottom';
    if (RX.dress.test(text)) return 'dress';
    if (RX.top.test(text) || RX.outer.test(text) || RX.knit.test(text)) return 'top';
    return null;
  }

  /* The product type decides first, the title only when the type says nothing (27-10-2026: a denim shirt, type shirts and
     tops, was read as trousers because of "jean" in its title, and got the hip table instead of its
     own bust measurements). Then what the chart measures. */
  function kindOf(type, title, has) {
    var k = kindByWords(type) || kindByWords(title);
    if (k) return k;
    if (has.foot && !has.chest && !has.hip) return 'shoe';
    if (has.chest) return 'top';
    if (has.hip || has.waist) return 'bottom';
    return 'top';
  }

  /* A maker who only says which weight each size is for (a recommended weight per size), with no measurement of the
     garment. Before 27-10-2026 such a chart was not read and the advice fell back to the standard table (wide
     jeans: up to two sizes smaller than the supplier's own table, which the shopper sees in the size guide). Now her
     body is turned into the weight that gives that body at the height the model is anchored on (166 cm, men 178 cm),
     her equivalent weight (weightOf), and placed in the supplier's ranges: at 166 cm that is exactly her weight, so the
     advice matches the table she reads; taller (slimmer) or shorter (fuller) moves it the way her build does.
     The rows in size order with their ranges, or null when there is no such chart or its ranges do not grow. */
  function fromWeight(sell, chart, notes) {
    if (chart.rows.some(function (r) { return r.v.chest != null || r.v.waist != null || r.v.hip != null; })) return null;
    var rows = sell.filter(function (r) { return r.v.wlo != null; }).map(function (r) { return { size: r.size, lo: r.v.wlo, hi: r.v.whi }; });
    if (rows.length < 2) return null;
    for (var i = 0; i < rows.length - 1; i++) {
      if (rows[i + 1].lo < rows[i].lo || rows[i + 1].hi < rows[i].hi) {
        notes.push('weight:order');
        return null;
      }
    }
    notes.push('weight');
    return rows;
  }

  /* The inverse of the body model at its anchor height: the weight whose estimated hips (men: chest) are hers. */
  function weightOf(body) {
    if (MC.kind === 'men') return 80 * Math.pow(body.chest / 100, 2);
    return 73 * Math.pow(body.hip / 103, 1 / 0.482);
  }

  /* And the other way round: the body of a weight at the anchor height. */
  function bodyAt(w) {
    if (MC.kind === 'men') {
      var c = BODY_MEN.chest(178, w);
      return { chest: c, waist: BODY_MEN.waist(c), hip: BODY_MEN.hip(c) };
    }
    var hip = BODY.hip(166, w);
    return { chest: BODY.chest(hip), waist: BODY.waist(hip), hip: hip };
  }

  function subOf(title, type) {
    if (RX.outer.test(title)) return 'outer';
    if (RX.knit.test(title)) return 'knit';
    if (RX.outer.test(type)) return 'outer';
    if (RX.knit.test(type)) return 'knit';
    return 'top';
  }

  function cutOf(pd, sub, title, kind) {
    if (!pd) return null;
    var T = pd.T;
    var cut;
    if (pd.dim === 'chest') {
      var t = sub === 'outer' ? [12, 24] : [8, 18];
      cut = T < 0 ? 'near' : T < t[0] ? 'fitted' : T < t[1] ? 'regular' : 'ample';
      /* The title's word for the cut wins over the numbers, unless the numbers say it hugs. */
      if (cut !== 'near' && (kind === 'top' || kind === 'dress') && RX.ample.test(title)) cut = 'ample';
    } else if (pd.dim === 'hip') {
      cut = T < -3 ? 'near' : T < 4 ? 'fitted' : T < 10 ? 'regular' : 'ample';
    } else {
      cut = T < 0 ? 'near' : T < 6 ? 'fitted' : 'regular';
    }
    return cut;
  }

  /* Everything that does not depend on the shopper: which kind of product, which measurements decide, how it is cut.
     prod: { title, type, sizes: [option values in order], chart: custom.size_chart value }. */
  MC.analyse = function (prod) {
    var sizes = (prod && prod.sizes ? prod.sizes : []).map(String);
    var title = String(prod && prod.title || '').toLowerCase();
    var type = String(prod && prod.type || '').toLowerCase();
    var a = { kind: 'one', sub: null, dims: [], notes: [], sizes: sizes, can: false, cut: null, std: null, shoe: null, inseam: null, zoneDims: [], shift: 0, byWeight: null };
    var learnt = Number(prod && prod.shift);
    if (isFinite(learnt)) a.shift = Math.max(-SHIFT_MAX, Math.min(SHIFT_MAX, learnt));
    if (sizes.length < 2 || sizes.every(function (s) { return RX.one.test(s); })) return a;
    var chart = readChart(prod.chart);
    a.measure = chart.measure;
    var byKey = {};
    chart.rows.forEach(function (r) { if (!byKey[r.key]) byKey[r.key] = r; });
    var sell = sizes.map(function (s) {
      var r = byKey[sizeKey(s)];
      return { size: s, std: stdOf(s), v: r ? r.v : {} };
    });
    var has = {};
    chart.rows.forEach(function (r) { Object.keys(r.v).forEach(function (k) { has[k] = true; }); });
    a.kind = kindOf(type, title, has);
    /* Where the kind came from, for the check script: a type without a known word leaves it to the title. */
    a.kindFrom = kindByWords(type) ? 'type' : kindByWords(title) ? 'title' : 'chart';
    if (a.kind === 'shoe') {
      a.shoe = prepShoe(sell, a.notes);
      a.can = a.shoe.rows.length >= 2;
      return a;
    }
    a.byWeight = fromWeight(sell, chart, a.notes);
    if (has.waist && has.hip) fixSwap(chart.rows, a.notes);
    a.sub = a.kind === 'top' ? subOf(title, type) : null;
    var want = { top: ['chest'], bottom: ['hip', 'waist'], dress: ['chest', 'waist', 'hip'], set: ['chest', 'hip', 'waist'] }[a.kind];
    var prepared = {};
    want.forEach(function (d) { prepared[d] = prepDim(chart.rows, sell, d, chart.measure, a.notes); });
    var pick;
    if (a.kind === 'top') pick = [prepared.chest];
    else if (a.kind === 'bottom') pick = [prepared.hip || prepared.waist];
    else if (a.kind === 'dress') pick = [prepared.chest, prepared.waist, prepared.hip];
    else pick = [prepared.chest, prepared.hip || prepared.waist];
    pick = pick.filter(Boolean);
    /* Several measurements decide together only over the sizes that carry all of them. */
    if (pick.length > 1) {
      var common = sell.filter(function (r) { return pick.every(function (pd) { return r.v[pd.dim] != null; }); });
      pick = pick.map(function (pd) { return prepDim(chart.rows, common, pd.dim, chart.measure, a.notes, true); }).filter(Boolean);
    }
    a.dims = pick;
    /* Every circumference the chart carries, for the fit per zone on the advice screen (MC.zones); not only the ones
       that decide. */
    a.zoneDims = ['chest', 'waist', 'hip'].map(function (d) {
      if (!has[d] || (a.kind === 'bottom' && d === 'chest')) return null;
      return prepDim(chart.rows, sell, d, chart.measure, a.notes, true);
    }).filter(Boolean);
    var lettered = sell.filter(function (r) { return r.std; });
    a.std = lettered.length >= 2 ? lettered : null;
    if (sell.some(function (r) { return r.v.inseam != null; })) a.inseam = sell;
    a.cut = cutOf(pick[0], a.sub, title, a.kind);
    a.wide = RX.wide.test(title) ? 5 : RX.narrow.test(title) ? 2 : 3;
    a.can = pick.length > 0 || !!a.std || !!a.byWeight;
    return a;
  };

  /* Her body in cm (chest, waist, hip), or null. p: { h, w, b, chest, waist, hip }. */
  MC.body = function (p) {
    if (!p) return null;
    var est = null;
    if (p.h >= 130 && p.h <= 210 && p.w >= 30 && p.w <= 200) {
      if (MC.kind === 'men') {
        var c = BODY_MEN.chest(p.h, p.w);
        est = { chest: c, waist: BODY_MEN.waist(c), hip: BODY_MEN.hip(c) };
      } else {
        var hip = BODY.hip(p.h, p.w);
        est = { chest: BODY.chest(hip), waist: BODY.waist(hip), hip: hip };
      }
      /* Her usual size (step 1, optional, owner 27-10-2026: "gewone maat als houvast"): the standard body of that size
         counts as much as her height and weight. */
      var u = /^(XXS|XS|S|M|L|XL|XXL|2XL|3XL|XXXL|4XL|5XL)$/i.test(String(p.u || '')) ? letterIndex(p.u) : -1;
      ['chest', 'waist', 'hip'].forEach(function (d) {
        if (u > -1) est[d] = (est[d] + STD[u][AXIS[d]]) / 2;
        var zone = p[ZONE_KEY[d]];
        if (p.zc || p.zw || p.zh) est[d] += ZONE[d][zone] || 0;
        else est[d] += (MC.kind === 'men' ? BODY_MEN : BODY).build[p.b] || 0;
      });
    }
    var out = { own: {}, est: !!est };
    var any = false;
    ['chest', 'waist', 'hip'].forEach(function (d) {
      var v = Number(p[d]);
      if (v >= 50 && v <= 200) {
        out[d] = v;
        out.own[d] = true;
        any = true;
      } else if (est) {
        out[d] = est[d];
        any = true;
      }
    });
    return any ? out : null;
  };

  /* Where her body plus the design ease falls on the chart, as a position in size steps: 2 is exactly the third size,
     2.5 halfway to the fourth. Beyond the ends it goes on with the grading step. */
  function place(vals, step, t) {
    var n = vals.length;
    if (t <= vals[0]) return -(vals[0] - t) / step;
    if (t >= vals[n - 1]) return (n - 1) + (t - vals[n - 1]) / step;
    for (var i = 0; i < n - 1; i++) {
      if (t <= vals[i + 1]) {
        var d = vals[i + 1] - vals[i];
        return d > 0 ? i + (t - vals[i]) / d : i;
      }
    }
    return n - 1;
  }

  /* One size from a position, and two when she sits in the middle third between them. */
  function decide(x, n) {
    if (x > n - 1 + 1) return { out: true, i: n - 1 };
    if (x > n - 1 + 0.5) return { i: n - 1, snug: true };
    if (x < -1) return { i: 0, roomy: true };
    if (x <= 0) return { i: 0 };
    var lo = Math.floor(x);
    if (lo >= n - 1) return { i: n - 1 };
    var f = x - lo;
    if (f < 0.3) return { i: lo };
    if (f > 0.7) return { i: lo + 1 };
    return { between: true, lo: lo, hi: lo + 1 };
  }

  /* A chart with only the supplier's weight per size (fromWeight): her equivalent weight in the supplier's ranges. Within
     1 kg of the edge between two sizes she sits between them, and the kind of garment or her preference decides, as on
     a measured chart. Her fit preference and what the exchanges taught move her by the same quarter steps, in kilos. */
  function adviseWeight(a, p, body, res) {
    var rows = a.byWeight;
    var n = rows.length;
    var fit = p && Object.prototype.hasOwnProperty.call(FIT, p.f) ? p.f : 'regular';
    var step = median(rows.map(function (r) { return r.hi - r.lo; }).filter(function (x) { return x > 0; })) || 7;
    var weq = weightOf(body) + (FIT[fit] + (a.shift || 0)) * step;
    res.basis = 'weight';
    res.status = 'ok';
    res.dim = 'weight';
    res.own = false;
    res.flags = { roomy: false, snug: false };
    res.alt = null;
    if (weq > rows[n - 1].hi) {
      res.pick = rows[n - 1].size;
      if (weq > rows[n - 1].hi + step / 2) res.status = 'out';
      else res.flags.snug = true;
      return res;
    }
    if (weq < rows[0].lo) {
      res.pick = rows[0].size;
      res.flags.roomy = weq < rows[0].lo - step / 2;
      return res;
    }
    var i = 0;
    while (i < n - 1 && weq > rows[i].hi) i++;
    var lo = i < n - 1 && rows[i].hi - weq < 1 ? i : i > 0 && weq - rows[i].lo < 1 ? i - 1 : -1;
    if (lo < 0) {
      res.pick = rows[i].size;
      return res;
    }
    var prefer = fit === 'near' ? 'down' : fit === 'loose' ? 'up' : a.kind === 'top' && a.cut === 'ample' ? 'down' : 'up';
    res.between = true;
    res.lower = rows[lo].size;
    res.upper = rows[lo + 1].size;
    res.pick = prefer === 'down' ? res.lower : res.upper;
    res.alt = prefer === 'down' ? res.upper : res.lower;
    res.prefer = prefer;
    return res;
  }

  function adviseClothes(a, p, res) {
    var body = MC.body(p);
    if (!body) return res;
    if (a.byWeight && !a.dims.length) return adviseWeight(a, p, body, res);
    var list;
    var sizes;
    if (a.dims.length) {
      res.basis = 'chart';
      sizes = a.dims[0].sizes;
      list = a.dims.map(function (pd) {
        var b = body[pd.dim];
        return b == null ? null : { pd: pd, b: b, x: place(pd.vals, pd.step, b + pd.T) };
      });
    } else {
      /* No measurement to trust: her usual size on the standard table. */
      res.basis = 'standard';
      sizes = a.std.map(function (r) { return r.size; });
      var axes = a.kind === 'bottom' ? ['hip'] : a.kind === 'top' ? ['chest'] : ['chest', 'hip'];
      list = axes.map(function (d) {
        var vals = a.std.map(function (r) { return r.std[AXIS[d]]; });
        return body[d] == null ? null : { pd: { dim: d, vals: vals, T: 0, step: 4, sizes: sizes }, b: body[d], x: place(vals, 4, body[d]) };
      });
    }
    if (list.some(function (e) { return !e; })) return res;
    var main = list[0];
    list.forEach(function (e) { if (e.x > main.x) main = e; });
    var n = sizes.length;
    var fit = p && Object.prototype.hasOwnProperty.call(FIT, p.f) ? p.f : 'regular';
    var shift = (fit === 'loose' && a.cut === 'ample') || (fit === 'near' && a.cut === 'near') ? 0 : FIT[fit];
    /* What the exchanges taught about this model (custom.maatcoach_shift) counts on top of her own preference. */
    shift += a.shift || 0;
    var dec = decide(main.x + shift, n);
    res.status = dec.out ? 'out' : 'ok';
    res.own = !!body.own[main.pd.dim];
    res.dim = main.pd.dim;
    res.cut = a.cut;
    res.sub = a.sub;
    res.flags = { roomy: !!dec.roomy, snug: !!dec.snug };
    /* An ample top keeps its look one size down; trousers keep room at the hips and length, so they go up. Her own
       preference, when she gave one, decides instead. */
    var prefer = fit === 'near' ? 'down' : fit === 'loose' ? 'up' : a.kind === 'top' && a.cut === 'ample' ? 'down' : 'up';
    if (dec.between) {
      res.between = true;
      res.lower = sizes[dec.lo];
      res.upper = sizes[dec.hi];
      res.pick = prefer === 'down' ? res.lower : res.upper;
      res.alt = prefer === 'down' ? res.upper : res.lower;
      res.prefer = prefer;
    } else {
      res.pick = sizes[dec.i];
      res.alt = null;
    }
    var pi = sizes.indexOf(res.pick);
    res.ease = res.basis === 'chart' && a.measure !== 'body' ? Math.round(main.pd.vals[pi] - main.b) : null;
    res.garment = res.basis === 'chart' ? Math.round(main.pd.vals[pi]) : null;
    res.body = Math.round(main.b);
    /* A set or a dress where the pieces disagree: name both. */
    if (list.length > 1) {
      var picks = list.map(function (e) { return sizes[Math.max(0, Math.min(n - 1, Math.round(e.x + shift)))]; });
      var differ = picks.some(function (s) { return s !== picks[0]; });
      if (differ) res.split = list.map(function (e, k) { return { dim: e.pd.dim, size: picks[k] }; });
    }
    /* Trousers: where the hem falls. Crotch height about 0.47 x height, the garment hangs a little below it. */
    if (a.inseam && p && p.h >= 130 && p.h <= 210) {
      var row = a.inseam.filter(function (r) { return r.size === res.pick; })[0];
      if (row && row.v.inseam) {
        var above = 0.47 * p.h - a.wide - row.v.inseam;
        res.hem = above <= 1 ? 'long' : above <= 9 ? 'ankle' : 'short';
      }
    }
    return res;
  }

  function adviseShoe(a, p, res) {
    var s = a.shoe;
    var st = SHOE[s.sys];
    var rows = s.rows;
    var n = rows.length;
    var foot = Number(p.foot) >= 18 && Number(p.foot) <= 32 ? Number(p.foot) : null;
    /* Her usual size, in the system she answered in (EU, unless the form asked her for US sizes); on a model in the
       other system it is carried over through the foot length. */
    var herSys = p.shoeSys === 'us' ? 'us' : 'eu';
    var own = Number(p.shoe) >= SHOE[herSys].her[0] && Number(p.shoe) <= SHOE[herSys].her[1] ? Number(p.shoe) : null;
    var usual = own == null ? null : shoeConvert(own, herSys, s.sys);
    if (foot == null && usual == null) return res;
    res.status = 'ok';
    res.flags = {};
    res.alt = null;
    if (foot != null && s.footOk) {
      /* Her foot against this model's foot length: the smallest size that takes it. */
      res.basis = 'foot';
      res.foot = foot;
      var i = -1;
      for (var k = 0; k < n; k++) {
        if (rows[k].foot != null && rows[k].foot >= foot - 0.1) { i = k; break; }
      }
      if (i === -1) {
        var last = rows[n - 1];
        if (last.foot != null && foot <= last.foot + 0.4) {
          res.pick = last.size;
          res.flags.snug = true;
        } else {
          res.status = 'out';
          res.pick = last.size;
        }
        return res;
      }
      res.pick = rows[i].size;
      res.col = rows[i].foot;
      /* Less than 0.3 cm to spare: fine barefoot or in thin socks, the next size up for thick ones. */
      if (rows[i].foot - foot < 0.3 && i < n - 1) {
        res.tight = true;
        res.alt = rows[i + 1].size;
      }
      return res;
    }
    if (usual == null) {
      /* Only her foot length, and a chart whose foot column cannot be read: the size that length usually is. */
      usual = Math.round(st.size(foot) * 2) / 2;
      res.basis = 'footStd';
      res.foot = foot;
    } else {
      res.basis = 'usual';
      res.given = { size: own, sys: herSys };
    }
    res.usual = usual;
    res.sys = s.sys;
    /* EU models come in whole sizes, so her half size takes the one up; US sizes have their halves. */
    var half = st.whole && usual % 1 !== 0;
    var want = half ? Math.ceil(usual) : usual;
    var idx = -1;
    for (var j = 0; j < n; j++) {
      if (want >= rows[j].lo && want <= rows[j].hi) { idx = j; break; }
    }
    if (idx === -1) {
      if (want < rows[0].lo) {
        res.pick = rows[0].size;
        res.flags.roomy = true;
        if (want < rows[0].lo - 1) res.status = 'out';
      } else if (want > rows[n - 1].hi) {
        res.pick = rows[n - 1].size;
        res.status = 'out';
      } else {
        /* Between two sizes of this model (US 7 where it has US 6.5 and US 7.5): the one up, for comfort, and the one
           below as the other choice. */
        for (var b = 1; b < n; b++) {
          if (rows[b].lo > want) {
            res.half = true;
            res.pick = rows[b].size;
            res.alt = rows[b - 1].size;
            break;
          }
        }
        if (!res.pick) {
          res.pick = rows[n - 1].size;
          res.status = 'out';
        }
      }
      return res;
    }
    res.half = half;
    if (s.footOk && rows[idx].foot != null && !half) {
      var d = rows[idx].foot - st.foot(usual);
      if (d <= -0.4 && idx < n - 1) {
        res.runs = 'small';
        res.pick = rows[idx + 1].size;
        res.alt = rows[idx].size;
        return res;
      }
      if (d >= 0.6) res.runs = 'large';
    }
    res.pick = rows[idx].size;
    if (half && idx > 0 && rows[idx - 1].hi < want) res.alt = rows[idx - 1].size;
    res.range = rows[idx].lo !== rows[idx].hi;
    return res;
  }

  /* The advice for this product and this shopper. status: 'ok', 'out' (beyond the largest size), 'need' (answers
     missing) or 'none' (nothing to advise on). */
  MC.advise = function (a, p) {
    var res = { status: 'need', kind: a.kind, pick: null, alt: null };
    if (!a.can) {
      res.status = 'none';
      return res;
    }
    if (a.kind === 'shoe') return adviseShoe(a, p || {}, res);
    return adviseClothes(a, p || {}, res);
  };

  /* How one size would fit her, zone by zone (the advice screen, owner 27-10-2026: "pasvorm per zone"). For every
     circumference the chart carries: her body plus the ease this model is cut with, against that size, in size steps.
     0 is how the model is meant to fit, below 0 closer to the body, above 0 roomier. Trousers also get the length from
     the inseam: below 0 shorter, above 0 longer. What the exchanges taught (a.shift) counts here too. No centimetres
     leave this function: the page shows a word and a dot (werkregel 9.10). */
  MC.zones = function (a, p, size) {
    var body = MC.body(p);
    var out = [];
    if (!body || !a || !a.zoneDims) return out;
    a.zoneDims.forEach(function (pd) {
      var i = pd.sizes.indexOf(size);
      if (i < 0 || body[pd.dim] == null) return;
      var garment = pd.vals[i] - (a.shift || 0) * pd.step;
      out.push({ zone: pd.dim, z: (garment - body[pd.dim] - pd.T) / pd.step });
    });
    if (a.inseam && p && p.h >= 130 && p.h <= 210) {
      var row = a.inseam.filter(function (r) { return r.size === size; })[0];
      if (row && row.v.inseam) out.push({ zone: 'length', z: (5 - (0.47 * p.h - a.wide - row.v.inseam)) / 7 });
    }
    return out;
  };

  /* Her size on the standard table for one measurement, as a letter; the check script compares the advice with it. */
  MC.standardSize = function (dim, value) {
    var axis = AXIS[dim];
    var best = 0;
    for (var i = 1; i < STD.length; i++) {
      if (Math.abs(STD[i][axis] - value) < Math.abs(STD[best][axis] - value)) best = i;
    }
    return LETTERS[best];
  };
  MC.letterIndex = letterIndex;
  MC.shoeConvert = shoeConvert;

  root.Maatcoach = MC;

  /* ======================================================================================================== */
  /* Page                                                                                                     */
  /* ======================================================================================================== */
  if (typeof document === 'undefined') return;

  var KEY = 'maatcoach-v1';

  function readProfile() {
    try {
      var p = JSON.parse(window.localStorage.getItem(KEY) || 'null');
      if (p && typeof p === 'object') return p;
    } catch (err) { /* storage blocked: the advice still works for this page */ }
    /* A store that also runs the jeans advice keeps her answers under its own key: take them over. */
    try {
      var f = JSON.parse(window.localStorage.getItem('fit-profile-v1') || 'null');
      if (f && f.height && f.weight) return { h: f.height, w: f.weight, b: { snella: 'slim', media: 'mid', morbida: 'full' }[f.shape] || 'mid', waist: f.waist, hip: f.hip };
    } catch (err) { /* idem */ }
    return {};
  }

  /* Thema 2 (03-10-2026): the size panels on product cards and in the cart rail ask for her answers too
     (assets/theme.js, advice on the plus). */
  MC.profile = readProfile;

  /* The profile on this device (owner 27-10-2026: "een profiel icon ... dat ze al hun gegevens kunnen invullen ...
     opgeslagen op basis van apparaat id"). Three keys, nothing is sent anywhere:
       maatcoach-v1      the answers, the profile in use; assets/theme.js and the menu (assets/verellia-menu.js) read and
                         write it too.
       verellia-profil   the first name (naam), age and the choice to send the profile with an order: where the menu keeps
                         them (snippets/menu-profil.liquid), so the menu and the coach greet him by one name.
       maatcoach-device  the device id. One profile per shopper (owner 27-10-2026: "elke klant krijgt maar 1 profiel"). */
  var ME = 'verellia-profil';
  var DEVICE = 'maatcoach-device';

  function load(key) {
    try {
      var v = JSON.parse(window.localStorage.getItem(key) || 'null');
      return v && typeof v === 'object' ? v : null;
    } catch (err) { return null; }
  }

  function keep(key, v) {
    try {
      if (v == null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, JSON.stringify(v));
    } catch (err) { /* private mode: the advice still works on this page */ }
  }

  function changed() {
    try { document.dispatchEvent(new CustomEvent('maatcoach:change')); } catch (err) { /* very old browser */ }
  }

  function uuid() {
    try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (err) { /* older browser */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function readMe() { return load(ME) || {}; }

  /* As the menu writes it: the key goes when nothing is left in it. */
  function saveMe(m) {
    var out = {};
    if (m.naam) out.naam = String(m.naam).slice(0, 30);
    if (m.age) out.age = m.age;
    if (m.delen) out.delen = m.delen;
    keep(ME, out.naam || out.age || out.delen ? out : null);
  }

  /* The device: its id, made on the first answer and shown in her profile. */
  function readDevice() {
    var d = load(DEVICE) || {};
    return d.id ? { id: d.id } : { id: uuid() };
  }

  function saveProfile(p) {
    keep(KEY, p);
    keep(DEVICE, readDevice());
    changed();
  }

  /* "Erase my data": the profile, the device id and the note on the bag (the menu puts the profile there when it was
     ticked). */
  function eraseAll() {
    var me = readMe();
    [KEY, ME, DEVICE, 'fit-profile-v1', 'maatcoach-units', 'maatcoach-units:metric', 'maatcoach-units:lb', 'maatcoach-units:st'].forEach(function (k) { keep(k, null); });
    if (me.delen && window.fetch) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.fetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: { Profile: '' } }) })
        .catch(function () { /* offline: the menu checks the note again on the next page */ });
    }
    changed();
  }

  /* Where she comes from, as Shopify sees it (/browsing_context_suggestions.json, the source of its Geolocation app), kept
     per session in 'detected-country' as snippets/country-redirect.liquid keeps it, so a page asks at most once. '' when
     Shopify does not know. */
  var visitorAsked = null;
  function visitorCountry() {
    try { return String(window.sessionStorage.getItem('detected-country') || '').toUpperCase(); } catch (err) { return ''; }
  }
  function askVisitorCountry() {
    if (!visitorAsked) {
      visitorAsked = visitorCountry() || !window.fetch ? Promise.resolve(visitorCountry()) : window.fetch('/browsing_context_suggestions.json?country[enabled]=true', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var land = j && j.detected_values && j.detected_values.country && j.detected_values.country.handle;
          if (land) { try { window.sessionStorage.setItem('detected-country', land); } catch (err) { /* no storage: the next page asks again */ } }
          return String(land || '').toUpperCase();
        })
        .catch(function () { return ''; });
    }
    return visitorAsked;
  }

  /* Feet, inches and pounds only for a visitor from a country that measures that way (owner 28-10-2026: "een duitse en
     nederlandse klant (EU) [zagen] inches maten ... Dat die automatisch het juiste eenheid pakt afhankelijk waar klant
     vandaan komt"): in a market with feet and pounds (data-mc-units lb or st) anyone from elsewhere starts in cm and kg. In
     a metric market the market decides, also for a visitor from abroad (owner 28-10-2026: "ik zit op frankrijk maar ik zie
     alleen ft of pond"). Country unknown: the market. The menu profile follows the same rule (assets/verellia-menu.js);
     change both together. */
  var IMPERIAL_COUNTRIES = /^(US|CA|LR|MM|GB)$/;
  function unitsFor(market, land) {
    return market === 'metric' || !land || IMPERIAL_COUNTRIES.test(land) ? market : 'metric';
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text) node.textContent = text;
    return node;
  }

  function button(cls, text) {
    var b = el('button', cls, text);
    b.type = 'button';
    return b;
  }

  function fill(text, values) {
    return String(text || '').replace(/%(\w+)%/g, function (match, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? values[name] : match;
    });
  }

  var ICONS = {
    ruler: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="8" rx="1.5"/><path d="M7 8v3M11 8v4M15 8v3"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
    back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>',
    go: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10.3"/><circle cx="12" cy="9.6" r="3.6"/><path d="M5.4 19.2c1.4-2.9 3.8-4.4 6.6-4.4s5.2 1.5 6.6 4.4"/></svg>',
    pen: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 20l1-4.5L15.8 4.7a1.8 1.8 0 0 1 2.5 0l1 1a1.8 1.8 0 0 1 0 2.5L8.5 19z"/><path d="M13.8 6.8l3.4 3.4"/></svg>',
    trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 7h15M10 7V4.8h4V7M6.5 7l1 12.5h9l1-12.5M10.2 10.5v6M13.8 10.5v6"/></svg>'
  };
  function icon(name, cls) {
    var span = el('span', cls || 'mc-hint__icon', '');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = ICONS[name] || '';
    return span;
  }

  function fmt(n, decimals, sep) {
    var s = String(Math.round(n * Math.pow(10, decimals)) / Math.pow(10, decimals));
    return sep === ',' ? s.replace('.', ',') : s;
  }

  function round2(v) { return Math.round(v * 100) / 100; }

  /* The figure per zone, drawn like a dress form (steps chest, stomach, hips, owner 27-10-2026): front view for chest
     and hips, side view for the stomach, the zone marked in the brand colour. */
  var FIG = {"chest":{"less":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17 20.7 17.8 23 C18.6 25.3 20.1 28.3 20.4 31 C20.7 33.7 19.4 36.3 19.6 39 C19.8 41.7 21.5 44.2 21.4 47 C21.3 49.8 19.8 53.5 19.2 56 C18.6 58.5 18 59.3 18 62 C18 64.7 18.6 68 19 72 C19.4 76 20.3 83.7 20.5 86 L39.5 86 C39.8 83.7 40.6 76 41 72 C41.4 68 42 64.7 42 62 C42 59.3 41.4 58.5 40.8 56 C40.2 53.5 38.7 49.8 38.6 47 C38.5 44.2 40.2 41.7 40.4 39 C40.6 36.3 39.3 33.7 39.6 31 C39.9 28.3 41.4 25.3 42.2 23 C43 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M23 30.5 Q25.2 33.3 29.2 33 M37 30.5 Q34.8 33.3 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M17 26 Q14.6 31 17 36 M43 26 Q45.4 31 43 36\" class=\"mc-fig__mark\"/></svg>","mid":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17.3 20.7 17.8 23 C18.3 25.3 18.1 28.3 18.4 31 C18.7 33.7 19.1 36.3 19.6 39 C20.1 41.7 21.5 44.2 21.4 47 C21.3 49.8 19.8 53.5 19.2 56 C18.6 58.5 18 59.3 18 62 C18 64.7 18.6 68 19 72 C19.4 76 20.3 83.7 20.5 86 L39.5 86 C39.8 83.7 40.6 76 41 72 C41.4 68 42 64.7 42 62 C42 59.3 41.4 58.5 40.8 56 C40.2 53.5 38.7 49.8 38.6 47 C38.5 44.2 39.9 41.7 40.4 39 C40.9 36.3 41.3 33.7 41.6 31 C41.9 28.3 41.7 25.3 42.2 23 C42.7 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M21 30.5 Q24.2 35.1 29.2 33 M39 30.5 Q35.8 35.1 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M15 26 Q12.6 31 15 36 M45 26 Q47.4 31 45 36\" class=\"mc-fig__mark\"/></svg>","more":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17.7 20.7 17.8 23 C17.9 25.3 15.7 28.3 16 31 C16.3 33.7 18.7 36.3 19.6 39 C20.5 41.7 21.5 44.2 21.4 47 C21.3 49.8 19.8 53.5 19.2 56 C18.6 58.5 18 59.3 18 62 C18 64.7 18.6 68 19 72 C19.4 76 20.3 83.7 20.5 86 L39.5 86 C39.8 83.7 40.6 76 41 72 C41.4 68 42 64.7 42 62 C42 59.3 41.4 58.5 40.8 56 C40.2 53.5 38.7 49.8 38.6 47 C38.5 44.2 39.5 41.7 40.4 39 C41.3 36.3 43.7 33.7 44 31 C44.3 28.3 42.1 25.3 42.2 23 C42.3 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M18.6 30.5 Q23 37.3 29.2 33 M41.4 30.5 Q37 37.3 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M12.6 26 Q10.2 31 12.6 36 M47.4 26 Q49.8 31 47.4 36\" class=\"mc-fig__mark\"/></svg>"},"waist":{"less":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.5 3 C26.4 4 26.3 7 26 9 C25.7 11 25 12.2 24.6 15 C24.2 17.8 23.8 22.2 23.8 26 C23.8 29.8 24.4 34.5 24.6 38 C24.8 41.5 25.4 43.7 25.2 47 C25 50.3 23.7 54.5 23.4 58 C23.1 61.5 23.1 63.3 23.2 68 C23.3 72.7 24 83 24.2 86 L33.8 86 C34.1 83.3 35.4 74.3 35.4 70 C35.4 65.7 34 63.5 34 60 C34 56.5 35.3 52.7 35.6 49 C35.9 45.3 35.3 41 35.6 38 C35.9 35 36.7 33.3 37.2 31 C37.7 28.7 38.7 26.7 38.4 24 C38.1 21.3 36.1 17.5 35.2 15 C34.3 12.5 33.5 11 33 9 C32.6 7 32.6 4 32.5 3 Z\" class=\"mc-fig__body\"/><path d=\"M38.8 44 Q41.2 49 38.8 54\" class=\"mc-fig__mark\"/></svg>","mid":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.5 3 C26.4 4 26.3 7 26 9 C25.7 11 25 12.2 24.6 15 C24.2 17.8 23.8 22.2 23.8 26 C23.8 29.8 24.4 34.5 24.6 38 C24.8 41.5 25.4 43.7 25.2 47 C25 50.3 23.7 54.5 23.4 58 C23.1 61.5 23.1 63.3 23.2 68 C23.3 72.7 24 83 24.2 86 L33.8 86 C34.1 83.3 35 74.3 35.4 70 C35.8 65.7 35.8 63.5 36.2 60 C36.6 56.5 37.9 52.7 37.8 49 C37.7 45.3 35.7 41 35.6 38 C35.5 35 36.7 33.3 37.2 31 C37.7 28.7 38.7 26.7 38.4 24 C38.1 21.3 36.1 17.5 35.2 15 C34.3 12.5 33.5 11 33 9 C32.6 7 32.6 4 32.5 3 Z\" class=\"mc-fig__body\"/><path d=\"M41 44 Q43.4 49 41 54\" class=\"mc-fig__mark\"/></svg>","more":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.5 3 C26.4 4 26.3 7 26 9 C25.7 11 25 12.2 24.6 15 C24.2 17.8 23.8 22.2 23.8 26 C23.8 29.8 24.4 34.5 24.6 38 C24.8 41.5 25.4 43.7 25.2 47 C25 50.3 23.7 54.5 23.4 58 C23.1 61.5 23.1 63.3 23.2 68 C23.3 72.7 24 83 24.2 86 L33.8 86 C34.1 83.3 34.4 74.3 35.4 70 C36.4 65.7 38.8 63.5 39.8 60 C40.8 56.5 42.1 52.7 41.4 49 C40.7 45.3 36.3 41 35.6 38 C34.9 35 36.7 33.3 37.2 31 C37.7 28.7 38.7 26.7 38.4 24 C38.1 21.3 36.1 17.5 35.2 15 C34.3 12.5 33.5 11 33 9 C32.6 7 32.6 4 32.5 3 Z\" class=\"mc-fig__body\"/><path d=\"M44.6 44 Q47 49 44.6 54\" class=\"mc-fig__mark\"/></svg>"},"hip":{"less":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17.3 20.7 17.8 23 C18.3 25.3 18.1 28.3 18.4 31 C18.7 33.7 19.1 36.3 19.6 39 C20.1 41.7 21.2 44.2 21.4 47 C21.6 49.8 20.9 53.5 20.6 56 C20.3 58.5 19.4 59.3 19.4 62 C19.4 64.7 20.2 68 20.4 72 C20.6 76 20.5 83.7 20.5 86 L39.5 86 C39.5 83.7 39.4 76 39.6 72 C39.8 68 40.6 64.7 40.6 62 C40.6 59.3 39.7 58.5 39.4 56 C39.1 53.5 38.4 49.8 38.6 47 C38.8 44.2 39.9 41.7 40.4 39 C40.9 36.3 41.3 33.7 41.6 31 C41.9 28.3 41.7 25.3 42.2 23 C42.7 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M21 30.5 Q24.2 35.1 29.2 33 M39 30.5 Q35.8 35.1 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M16 57 Q13.6 62 16 67 M44 57 Q46.4 62 44 67\" class=\"mc-fig__mark\"/></svg>","mid":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17.3 20.7 17.8 23 C18.3 25.3 18.1 28.3 18.4 31 C18.7 33.7 19.1 36.3 19.6 39 C20.1 41.7 21.5 44.2 21.4 47 C21.3 49.8 19.6 53.5 19 56 C18.4 58.5 17.8 59.3 17.8 62 C17.8 64.7 18.4 68 18.8 72 C19.3 76 20.2 83.7 20.5 86 L39.5 86 C39.8 83.7 40.8 76 41.2 72 C41.7 68 42.2 64.7 42.2 62 C42.2 59.3 41.6 58.5 41 56 C40.4 53.5 38.7 49.8 38.6 47 C38.5 44.2 39.9 41.7 40.4 39 C40.9 36.3 41.3 33.7 41.6 31 C41.9 28.3 41.7 25.3 42.2 23 C42.7 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M21 30.5 Q24.2 35.1 29.2 33 M39 30.5 Q35.8 35.1 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M14.4 57 Q12 62 14.4 67 M45.6 57 Q48 62 45.6 67\" class=\"mc-fig__mark\"/></svg>","more":"<svg viewBox=\"0 0 60 90\" aria-hidden=\"true\"><path d=\"M26.4 3 C26.3 4 27.3 7.3 25.8 9 C24.3 10.7 19.2 11.7 17.5 13 C15.8 14.3 15.5 15.3 15.5 17 C15.6 18.7 17.3 20.7 17.8 23 C18.3 25.3 18.1 28.3 18.4 31 C18.7 33.7 19.1 36.3 19.6 39 C20.1 41.7 21.9 44.2 21.4 47 C20.9 49.8 17.8 53.5 16.8 56 C15.8 58.5 15.6 59.3 15.6 62 C15.6 64.7 15.8 68 16.6 72 C17.4 76 19.9 83.7 20.5 86 L39.5 86 C40.2 83.7 42.6 76 43.4 72 C44.2 68 44.4 64.7 44.4 62 C44.4 59.3 44.2 58.5 43.2 56 C42.2 53.5 39.1 49.8 38.6 47 C38.1 44.2 39.9 41.7 40.4 39 C40.9 36.3 41.3 33.7 41.6 31 C41.9 28.3 41.7 25.3 42.2 23 C42.7 20.7 44.5 18.7 44.5 17 C44.6 15.3 44.2 14.3 42.5 13 C40.8 11.7 35.7 10.7 34.2 9 C32.7 7.3 33.7 4 33.6 3 Z\" class=\"mc-fig__body\"/><path d=\"M21 30.5 Q24.2 35.1 29.2 33 M39 30.5 Q35.8 35.1 30.8 33\" class=\"mc-fig__line\"/><circle cx=\"30\" cy=\"51\" r=\"0.7\" class=\"mc-fig__dot\"/><path d=\"M12.2 57 Q9.8 62 12.2 67 M47.8 57 Q50.2 62 47.8 67\" class=\"mc-fig__mark\"/></svg>"}};

  /* How she likes her clothes to fit: a top, narrow, regular or wide. */
  var FIT_ICON = {
    near: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M15 7h10l6 4-2 5-4-2v20H15V14l-4 2-2-5z"/><path d="M17.5 7c1 1.6 4 1.6 5 0"/></svg>',
    regular: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M14 7h12l7 4-2.5 5-3.5-2v20H13V14l-3.5 2L7 11z"/><path d="M17 7c1.2 1.8 4.8 1.8 6 0"/></svg>',
    loose: '<svg viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M13 7h14l9 5-3 6-3-2v18H10V16l-3 2-3-6z"/><path d="M16.5 7c1.4 2 5.6 2 7 0"/></svg>'
  };

  /* The sizes she can name as her usual one; the shop's own letters. */
  var USUAL = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  /* Her own silhouette as the avatar of her profile (owner 27-10-2026: "soort van eigen persoons avatar"): the dress form of
     the figures, with the bust and the hips she chose. A smooth line through half-widths. */
  function spline(pts) {
    var r = function (v) { return Math.round(v * 10) / 10; };
    var d = 'M' + r(pts[0][0]) + ' ' + r(pts[0][1]);
    for (var i = 0; i < pts.length - 1; i++) {
      var p0 = pts[i - 1] || pts[i];
      var p1 = pts[i];
      var p2 = pts[i + 1];
      var p3 = pts[i + 2] || p2;
      d += ' C' + r(p1[0] + (p2[0] - p0[0]) / 6) + ' ' + r(p1[1] + (p2[1] - p0[1]) / 6) + ' ' + r(p2[0] - (p3[0] - p1[0]) / 6) + ' ' + r(p2[1] - (p3[1] - p1[1]) / 6) + ' ' + r(p2[0]) + ' ' + r(p2[1]);
    }
    return d;
  }
  function avatar(p) {
    var B = { less: 9.6, mid: 11.6, more: 14 }[p.zc] || 11.6;
    var H = { less: 10.6, mid: 12.2, more: 14.4 }[p.zh] || 12.2;
    var half = [[3.6, 3], [4.2, 9], [12.5, 13], [14.5, 17], [12.2, 23], [B, 31], [10.4, 39], [8.6, 47], [H - 1.2, 56], [H, 62], [H - 1, 72], [9.5, 86]];
    var left = half.map(function (q) { return [30 - q[0], q[1]]; });
    var right = half.map(function (q) { return [30 + q[0], q[1]]; }).reverse();
    var body = spline(left) + ' L' + right[0][0] + ' ' + right[0][1] + ' ' + spline(right).replace(/^M[^C]*/, '') + ' Z';
    var dip = Math.round((35 + (B - 11.5) * 0.9) * 10) / 10;
    var cup = 'M' + (30 - B + 2.6) + ' 30.5 Q' + (30 - B / 2) + ' ' + dip + ' 29.2 33 M' + (30 + B - 2.6) + ' 30.5 Q' + (30 + B / 2) + ' ' + dip + ' 30.8 33';
    return '<svg viewBox="0 0 60 90" aria-hidden="true"><path d="' + body + '" class="mc-fig__body"/><path d="' + cup + '" class="mc-fig__line"/></svg>';
  }

  /* A ruler she swipes (owner 27-10-2026: "klanten moeten hun lengte en gewicht kunnen swipen"). A row of ticks scrolls
     sideways under a fixed mark; the value under the mark stands in a small box above it, as on the screens of Icon
     Amsterdam. Native scrolling with snap points does the swipe, the fling and the landing on a whole value; a mouse
     drags it or clicks a tick; − and + step one value (held down they repeat), and so do the arrow keys.
     cfg: label, min, max, step, value, tick (px between two values), major(v) (label under a long tick, or null), mid(v),
     show(v) (the text in the box), onInput(v). words: less, more. */
  function Ruler(host, cfg, words) {
    var n = Math.round((cfg.max - cfg.min) / cfg.step);
    var tw = cfg.tick || 10;
    var index = 0;
    var quiet = 0;
    var raf = 0;
    var settle = 0;
    host.innerHTML = '';
    host.className = 'mc-dial';
    host.style.setProperty('--mc-tick', tw + 'px');

    var less = button('mc-dial__btn', '−');
    var more = button('mc-dial__btn', '+');
    less.setAttribute('aria-label', words.less + ' (' + cfg.label + ')');
    more.setAttribute('aria-label', words.more + ' (' + cfg.label + ')');
    var win = el('div', 'mc-dial__window');
    var box = el('span', 'mc-dial__box');
    box.setAttribute('aria-hidden', 'true');
    var track = el('div', 'mc-dial__track');
    track.tabIndex = 0;
    track.setAttribute('role', 'slider');
    track.setAttribute('aria-label', cfg.label);
    var scale = el('div', 'mc-dial__scale');
    var frag = document.createDocumentFragment();
    for (var i = 0; i <= n; i++) {
      var v = round2(cfg.min + i * cfg.step);
      var tick = el('span', 'mc-tick');
      var name = cfg.major(v);
      if (name != null) {
        tick.className += ' mc-tick--major';
        tick.appendChild(el('span', 'mc-tick__label', name));
      } else if (cfg.mid && cfg.mid(v)) {
        tick.className += ' mc-tick--mid';
      }
      frag.appendChild(tick);
    }
    scale.appendChild(frag);
    track.appendChild(scale);
    win.appendChild(track);
    win.appendChild(box);
    win.appendChild(el('span', 'mc-dial__mark'));
    host.appendChild(less);
    host.appendChild(win);
    host.appendChild(more);

    function value() { return round2(cfg.min + index * cfg.step); }

    function paint() {
      var v = value();
      var text = cfg.show(v);
      box.textContent = text;
      track.setAttribute('aria-valuemin', String(cfg.min));
      track.setAttribute('aria-valuemax', String(cfg.max));
      track.setAttribute('aria-valuenow', String(v));
      track.setAttribute('aria-valuetext', text);
      less.disabled = index <= 0;
      more.disabled = index >= n;
    }

    /* Puts the tick of the value under the mark. Hidden there is nothing to scroll: sync() does it once it shows. */
    function place(smooth) {
      if (!track.clientWidth) return;
      quiet++;
      if (smooth && track.scrollTo) track.scrollTo({ left: index * tw, behavior: 'smooth' });
      else track.scrollLeft = index * tw;
      setTimeout(function () { quiet = Math.max(0, quiet - 1); }, smooth ? 450 : 60);
    }

    function go(i, smooth) {
      i = Math.max(0, Math.min(n, i));
      var changed = i !== index;
      index = i;
      paint();
      place(smooth);
      if (changed) cfg.onInput(value());
    }

    function read() {
      if (!track.clientWidth) return;
      var i = Math.max(0, Math.min(n, Math.round(track.scrollLeft / tw)));
      if (i === index) return;
      index = i;
      paint();
      cfg.onInput(value());
    }

    track.addEventListener('scroll', function () {
      clearTimeout(settle);
      settle = setTimeout(read, 120);
      if (quiet || raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        read();
      });
    }, { passive: true });

    var drag = null;
    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { x: event.clientX, left: track.scrollLeft, moved: false };
      track.classList.add('is-dragging');
      try { track.setPointerCapture(event.pointerId); } catch (err) { /* dragging still works over the track */ }
      event.preventDefault();
      try { track.focus({ preventScroll: true }); } catch (err) { track.focus(); }
    });
    track.addEventListener('pointermove', function (event) {
      if (!drag) return;
      var dx = event.clientX - drag.x;
      if (Math.abs(dx) > 3) drag.moved = true;
      track.scrollLeft = drag.left - dx;
    });
    function drop(event) {
      if (!drag) return;
      var d = drag;
      drag = null;
      track.classList.remove('is-dragging');
      if (!d.moved && event.type === 'pointerup') {
        var r = track.getBoundingClientRect();
        go(Math.round((track.scrollLeft + event.clientX - r.left - track.clientWidth / 2) / tw), true);
        return;
      }
      go(Math.round(track.scrollLeft / tw), true);
    }
    track.addEventListener('pointerup', drop);
    track.addEventListener('pointercancel', drop);

    track.addEventListener('keydown', function (event) {
      var k = event.key;
      var d = 0;
      if (k === 'ArrowLeft' || k === 'ArrowDown') d = -1;
      else if (k === 'ArrowRight' || k === 'ArrowUp') d = 1;
      else if (k === 'PageDown') d = -10;
      else if (k === 'PageUp') d = 10;
      else if (k === 'Home') d = -n;
      else if (k === 'End') d = n;
      if (!d) return;
      event.preventDefault();
      go(index + d, false);
    });

    function stepper(btn, d) {
      var hold = 0;
      var repeat = 0;
      var held = false;
      function stop() {
        clearTimeout(hold);
        clearInterval(repeat);
        hold = 0;
        repeat = 0;
      }
      btn.addEventListener('pointerdown', function (event) {
        if (event.button !== 0) return;
        held = false;
        stop();
        hold = setTimeout(function () {
          held = true;
          repeat = setInterval(function () {
            if ((d < 0 && index <= 0) || (d > 0 && index >= n)) { stop(); return; }
            go(index + d, false);
          }, 70);
        }, 380);
      });
      ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (type) { btn.addEventListener(type, stop); });
      btn.addEventListener('click', function () {
        if (held) {
          held = false;
          return;
        }
        go(index + d, true);
      });
    }
    stepper(less, -1);
    stepper(more, 1);

    index = Math.max(0, Math.min(n, Math.round((cfg.value - cfg.min) / cfg.step)));
    paint();

    this.sync = function () { place(false); };
    this.value = value;
  }

  /* One coach, in the size guide pop-up of a product page. In steps, one question per screen and one tap per answer (owner 27-10-2026: "heel duidelijk ... makkelijk in gebruik ... voor
     elke doelgroep. Hou het compact ... less is more niet teveel tekst"): height and
     weight on a ruler with the answer on the button, the usual size, bust, stomach and hips as figures, the fit, and then
     one size with a button to add it to the bag. The answers are kept in this browser and count on every product. */
  function Coach(box) {
    var S;
    var data = null;
    try { S = JSON.parse(box.querySelector('script[data-mc-strings]').textContent); } catch (err) { return; }
    /* The t filter hands over escaped text ("s&#39;affiche"); the script writes it as plain text, so decode once. */
    var decoder = document.createElement('textarea');
    Object.keys(S).forEach(function (k) {
      if (typeof S[k] !== 'string') return;
      decoder.innerHTML = S[k];
      S[k] = decoder.value;
    });
    /* The looks page (28-10-2026, owner: "De nieuwe maatcoach moeten we ook nog eens gaan toevoegen bij the looks
       ... Klant vult daar dus eenmalig haar gegevens en alle data wordt dan aangevuld"): a box with data-mc-look belongs to no
       product. Same questions, and at the end her size for every piece of the open outfit, each from its own chart
       (assets/looks.js hands the pieces over in box.mcLook and fills in the sizes of the outfit). */
    var look = box.hasAttribute('data-mc-look');
    if (look) data = { title: '', type: '', sizes: [], chart: null, colorIndex: -1 };
    else {
      try { data = JSON.parse(box.querySelector('script[data-mc-product]').textContent); } catch (err) { return; }
    }
    MC.setBody(box.getAttribute('data-mc-body'));
    var men = MC.kind === 'men';
    var a = look ? { kind: 'look', can: true, sizes: [] } : MC.analyse(data);
    if (!a.can) return;
    var hint = look ? null : document.querySelector('[data-mc-hint]');
    var advice = !!hint && hint.hasAttribute('data-mc-advice');
    if (!(data.colorIndex >= 0)) {
      var section = document.querySelector('[data-color-index]');
      data.colorIndex = section ? parseInt(section.getAttribute('data-color-index'), 10) : -1;
    }
    var shoe = a.kind === 'shoe';
    var sep = S.decimal || ',';
    var image = box.getAttribute('data-mc-image') || '';
    var dialog = box.closest('dialog');
    box.hidden = false;
    /* Thema 2 (02-10-2026): on a product the coach can advise on, the size guide shows the coach alone. */
    if (dialog) dialog.classList.add('sg--coach');

    var steps = [];
    /* Her shoe size closes the steps on clothing too, until she gives it or skips it (owner 27-10-2026: "Ik mis trouwens nog
       de schoenmaat"); on shoes it is the only question. */
    function stepList() {
      if (shoe) return ['shoe'];
      var noShoe = !(Number(profile.shoe) > 0);
      /* An outfit: what she answered before stays, so with her body known only a missing shoe size is asked. On the looks
         page the shoe size is always asked while it is missing, also after she skipped it on clothing (owner 28-10-2026:
         "Ik mis hier ook nog schoenmaat"). */
      if (look && MC.body(profile)) return noShoe ? ['shoe'] : [];
      /* Her age after her height and weight (owner 28-10-2026: "In de maatcoach mis ik trouwens nog leeftijd bij het invullen
         van hun profiel"). */
      var list = ['h', 'w', 'age', 'u', 'zc', 'zw', 'zh', 'f'];
      if (noShoe && (look || !profile.shoeSkip)) list.push('shoe');
      return list;
    }
    var end = look ? 'look' : 'result';
    var profile = readProfile();
    steps = stepList();

    /* The rulers follow her market (snippets/maatcoach.liquid, data-mc-units: metric, lb for the United States and
       Canada, st for the United Kingdom). Answers are stored in cm and kg. Every market has a switch to the other system,
       and her choice holds only in the market where she made it (owner 28-10-2026: "ik zit op frankrijk maar ik zie alleen
       ft of pond ipv europese maten. ik mis hier nog steeds de opties ... kunnen kiezen voor ft of lb"): a choice for feet
       and pounds on /en-us no longer turns up in France, and the market alone decides where she starts, not the country of
       her connection (a VPN, a holiday). Since 28-10-2026 a market with feet and pounds counts only for a visitor from a
       country that measures that way (unitsFor above): someone from the Netherlands on /en-gb starts in cm and kg. */
    var market = box.getAttribute('data-mc-units') || 'metric';
    var marketUnits, UNITS, imperial;
    function decideUnits() {
      marketUnits = unitsFor(market, visitorCountry());
      UNITS = 'maatcoach-units:' + marketUnits;
      var unitPref = null;
      try { unitPref = window.localStorage.getItem(UNITS); } catch (err) { /* storage blocked: the market decides */ }
      imperial = unitPref === 'imperial' || (unitPref !== 'metric' && marketUnits !== 'metric');
    }
    decideUnits();
    function shoeSys() { return imperial && marketUnits === 'lb' ? 'us' : 'eu'; }
    /* Her country is usually known before she opens the coach (snippets/country-redirect.liquid asks on the first page, the
       ask below on page load). Should it arrive while a step is open, that step is drawn again in her units. */
    if (market !== 'metric' && !visitorCountry()) {
      askVisitorCountry().then(function () {
        var wasUnits = marketUnits, wasImperial = imperial;
        decideUnits();
        if (at && (wasUnits !== marketUnits || wasImperial !== imperial)) show(at, 0);
      });
    }

    var BUILD = { slim: 'less', mid: 'mid', full: 'more' };
    var draft = {};
    function startDraft() {
      var old = BUILD[profile.b] || 'mid';
      draft = {
        h: profile.h >= 130 && profile.h <= 210 ? profile.h : (men ? 178 : 165),
        w: profile.w >= 30 && profile.w <= 200 ? profile.w : (men ? 80 : 65),
        u: profile.u || null,
        zc: profile.zc || old,
        zw: profile.zw || old,
        zh: profile.zh || old,
        f: profile.f || 'regular',
        shoe: Number(profile.shoe) > 0 ? Number(profile.shoe) : null,
        shoeSys: profile.shoeSys === 'us' ? 'us' : 'eu'
      };
    }

    /* The frame: back arrow, progress bar and the step count, then the screen. */
    var ui = el('div', 'mc__ui');
    var top = el('div', 'mc__top');
    var prev = button('mc__prev', '');
    prev.setAttribute('aria-label', S.back);
    prev.appendChild(icon('back', 'mc__prev-icon'));
    var bar = el('span', 'mc__bar');
    var barFill = el('i', '');
    bar.appendChild(barFill);
    var count = el('span', 'mc__count');
    top.appendChild(prev);
    top.appendChild(bar);
    top.appendChild(count);
    var screen = el('div', 'mc__screen');
    ui.appendChild(top);
    ui.appendChild(screen);
    box.appendChild(ui);
    var at = null;
    var ruler = null;
    var editing = false;
    var from = null;

    /* The head of the pop-up: the profile on the left, the logo of the shop in the middle, the cross on the right (owner
       27-10-2026: "een branded logo een profiel icon"). The title stays for
       screen readers. On her profile, and on an answer she changes from it, the left button goes back. */
    var meBtn = null;
    var head = dialog && dialog.querySelector('.sg__head');
    if (head && !head.querySelector('.mc-head__me')) {
      head.classList.add('mc-head');
      meBtn = button('mc-head__me', '');
      head.insertBefore(meBtn, head.firstChild);
      var logo = box.getAttribute('data-mc-logo');
      var title = head.querySelector('.sg__title');
      if (logo && title) {
        var words = el('span', 'visually-hidden', title.textContent.trim());
        var img = el('img', 'mc-head__logo');
        img.src = logo;
        img.alt = '';
        title.innerHTML = '';
        title.appendChild(img);
        title.appendChild(words);
      }
      meBtn.addEventListener('click', function () {
        if (editing) {
          editing = false;
          show('me', -1);
        } else if (at === 'me') {
          show(from || end, -1);
        } else {
          from = at;
          show('me', 1);
        }
      });
    }

    function commit(fields) {
      Object.keys(fields).forEach(function (k) {
        if (fields[k] == null) delete profile[k];
        else profile[k] = fields[k];
      });
      profile.t = Date.now();
      saveProfile(profile);
    }

    function next() {
      if (editing) {
        editing = false;
        show('me', -1);
        return;
      }
      var i = steps.indexOf(at);
      if (i > -1 && i < steps.length - 1) show(steps[i + 1], 1);
      else show(end, 1);
    }

    prev.addEventListener('click', function () {
      var i = steps.indexOf(at);
      if (i > 0) show(steps[i - 1], -1);
    });

    function show(name, dir) {
      at = name;
      ruler = null;
      screen.innerHTML = '';
      if (meBtn) {
        var away = name === 'me' || editing;
        meBtn.innerHTML = '';
        meBtn.appendChild(icon(away ? 'back' : 'user', 'mc-head__icon'));
        meBtn.setAttribute('aria-label', away ? S.back : S.pTitle);
      }
      var i = steps.indexOf(name);
      top.hidden = i < 0 || steps.length < 2 || editing;
      if (i > -1) {
        prev.style.visibility = i === 0 ? 'hidden' : '';
        barFill.style.width = ((i + 1) / steps.length * 100) + '%';
        count.textContent = fill(S.count, { step: i + 1, total: steps.length });
      }
      var node = el('div', 'mc__step ' + (dir < 0 ? 'mc__step--back' : 'mc__step--in'));
      var draw = { h: stepHeight, w: stepWeight, u: stepUsual, zc: stepZone, zw: stepZone, zh: stepZone, f: stepFit, shoe: stepShoe, age: stepAge, result: stepResult, look: stepLook, me: stepMe, n: stepName }[name];
      if (draw) draw(node, name);
      screen.appendChild(node);
      if (ruler) window.requestAnimationFrame(ruler.sync);
      var scroller = box.closest('.sg__body');
      if (scroller) scroller.scrollTop = 0;
      if (dir) {
        var title = node.querySelector('.mc__q, .mc__label, .mc-me__name');
        if (title) {
          title.tabIndex = -1;
          try { title.focus({ preventScroll: true }); } catch (err) { title.focus(); }
        }
      }
    }

    function question(node, text) { node.appendChild(el('h3', 'mc__q', text)); }

    /* The answer is on the button ("I'm 183 cm"). */
    function goButton(node, text, onGo) {
      var b = button('btn btn--full mc__go', '');
      var label = el('span', '', text);
      b.appendChild(label);
      b.appendChild(icon('go', 'mc__go-icon'));
      b.addEventListener('click', onGo);
      node.appendChild(b);
      return label;
    }

    function unitsToggle(node) {
      var t = button('mc__units', S[imperial ? 'unitsMetric' : 'unitsImperial']);
      t.addEventListener('click', function () {
        imperial = !imperial;
        try { window.localStorage.setItem(UNITS, imperial ? 'imperial' : 'metric'); } catch (err) { /* idem */ }
        show(at, 0);
      });
      node.appendChild(t);
    }

    function hero(node) {
      if (!image || steps[0] !== at || editing) return;
      var h = el('div', 'mc__hero');
      var img = el('img', '');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      h.appendChild(img);
      if (S.hero) h.appendChild(el('p', 'mc__hero-line', S.hero));
      node.appendChild(h);
    }

    function feet(cm) { var i = Math.round(cm / 2.54); return Math.floor(i / 12) + '′' + (i % 12) + '″'; }
    function heightText(cm) { return imperial ? feet(cm) : Math.round(cm) + ' cm'; }
    function weightText(kg) {
      if (!imperial) return Math.round(kg) + ' kg';
      var lb = Math.round(kg / 0.45359237);
      return marketUnits === 'st' ? Math.floor(lb / 14) + ' st ' + (lb % 14) + ' lb' : lb + ' lb';
    }

    function stepHeight(node) {
      hero(node);
      question(node, S.qHeight);
      var host = el('div', '');
      node.appendChild(host);
      var label;
      ruler = new Ruler(host, imperial ? {
        label: S.qHeight, min: 51, max: 82, step: 1, tick: 16, value: Math.round(draft.h / 2.54),
        major: function (v) { return v % 12 === 0 ? (v / 12) + '′' : null; },
        mid: function (v) { return v % 6 === 0; },
        show: function (v) { return Math.floor(v / 12) + '′' + (v % 12) + '″'; },
        onInput: function (v) { draft.h = Math.round(v * 2.54); label.textContent = fill(S.goHeight, { v: heightText(draft.h) }); }
      } : {
        label: S.qHeight, min: 130, max: 210, step: 1, tick: 10, value: Math.round(draft.h),
        major: function (v) { return v % 10 === 0 ? String(v) : null; },
        mid: function (v) { return v % 5 === 0; },
        show: function (v) { return String(v); },
        onInput: function (v) { draft.h = v; label.textContent = fill(S.goHeight, { v: heightText(v) }); }
      }, S);
      unitsToggle(node);
      label = goButton(node, fill(S.goHeight, { v: heightText(draft.h) }), function () {
        commit({ h: draft.h });
        next();
      });
    }

    function stepWeight(node) {
      question(node, S.qWeight);
      var host = el('div', '');
      node.appendChild(host);
      var label;
      var st = marketUnits === 'st';
      ruler = new Ruler(host, imperial ? {
        label: S.qWeight, min: 67, max: 440, step: 1, tick: 8, value: Math.round(draft.w / 0.45359237),
        major: function (v) { return st ? (v % 14 === 0 ? (v / 14) + ' st' : null) : (v % 10 === 0 ? String(v) : null); },
        mid: function (v) { return st ? v % 7 === 0 : v % 5 === 0; },
        show: function (v) { return st ? Math.floor(v / 14) + ' st ' + (v % 14) : String(v); },
        onInput: function (v) { draft.w = Math.round(v * 0.45359237 * 10) / 10; label.textContent = fill(S.goWeight, { v: weightText(draft.w) }); }
      } : {
        label: S.qWeight, min: 30, max: 200, step: 1, tick: 10, value: Math.round(draft.w),
        major: function (v) { return v % 10 === 0 ? String(v) : null; },
        mid: function (v) { return v % 5 === 0; },
        show: function (v) { return String(v); },
        onInput: function (v) { draft.w = v; label.textContent = fill(S.goWeight, { v: weightText(v) }); }
      }, S);
      unitsToggle(node);
      label = goButton(node, fill(S.goWeight, { v: weightText(draft.w) }), function () {
        commit({ w: draft.w });
        next();
      });
    }

    /* One tap answers and goes on, with a moment to see the choice light up. */
    function tapThen(btn, group, after) {
      btn.addEventListener('click', function () {
        var all = group.querySelectorAll('[aria-pressed]');
        for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', all[i] === btn ? 'true' : 'false');
        after();
        setTimeout(next, 180);
      });
    }

    function stepUsual(node) {
      question(node, S.qUsual);
      var grid = el('div', 'mc-chips');
      USUAL.forEach(function (s) {
        var b = button('mc-chip', s);
        b.setAttribute('aria-pressed', draft.u === s ? 'true' : 'false');
        tapThen(b, grid, function () {
          draft.u = s;
          commit({ u: s });
        });
        grid.appendChild(b);
      });
      node.appendChild(grid);
      var none = button('mc__none', S.usualNone);
      none.addEventListener('click', function () {
        draft.u = null;
        commit({ u: null });
        next();
      });
      node.appendChild(none);
    }

    var ZONES = { zc: 'chest', zw: 'waist', zh: 'hip' };
    function stepZone(node, k) {
      var zone = ZONES[k];
      var key = zone === 'chest' && men ? 'ChestMen' : zone.charAt(0).toUpperCase() + zone.slice(1);
      question(node, S['q' + (zone === 'chest' && men ? 'ChestMen' : key)] || S['q' + key]);
      var row = el('div', 'mc-figs');
      ['less', 'mid', 'more'].forEach(function (o) {
        var b = button('mc-fig', '');
        b.setAttribute('aria-pressed', draft[k] === o ? 'true' : 'false');
        var fig = el('span', 'mc-fig__art');
        fig.innerHTML = FIG[zone][o];
        b.appendChild(fig);
        b.appendChild(el('span', 'mc-fig__name', S['o' + key + o.charAt(0).toUpperCase() + o.slice(1)]));
        tapThen(b, row, function () {
          draft[k] = o;
          var f = {};
          f[k] = o;
          commit(f);
        });
        row.appendChild(b);
      });
      node.appendChild(row);
    }

    function stepFit(node) {
      question(node, S.qFit);
      var list = el('div', 'mc-fits');
      [['near', S.fitNear], ['regular', S.fitRegular], ['loose', S.fitLoose]].forEach(function (o) {
        var b = button('mc-fitopt', '');
        b.setAttribute('aria-pressed', draft.f === o[0] ? 'true' : 'false');
        var ic = el('span', 'mc-fitopt__icon');
        ic.innerHTML = FIT_ICON[o[0]];
        b.appendChild(ic);
        b.appendChild(el('span', 'mc-fitopt__name', o[1]));
        tapThen(b, list, function () {
          draft.f = o[0];
          commit({ f: o[0] });
        });
        list.appendChild(b);
      });
      node.appendChild(list);
    }

    function stepShoe(node) {
      hero(node);
      question(node, S.qShoe);
      var host = el('div', '');
      node.appendChild(host);
      var sys = shoeSys();
      var us = sys === 'us';
      var usual = draft.shoe ? MC.shoeConvert(draft.shoe, draft.shoeSys, sys) : (us ? (men ? 9 : 7.5) : (men ? 42 : 38));
      draft.shoe = usual;
      draft.shoeSys = sys;
      var text = function (v) { return (us ? 'US ' : '') + fmt(v, 1, us ? '.' : sep); };
      var label;
      ruler = new Ruler(host, {
        label: S.qShoe, min: us ? (men ? 6 : 4) : (men ? 38 : 34), max: us ? (men ? 14 : 12) : (men ? 48 : 45), step: 0.5, tick: 18, value: usual,
        major: function (v) { return v % 1 === 0 ? String(v) : null; },
        show: text,
        onInput: function (v) { draft.shoe = v; label.textContent = fill(S.goShoe, { v: text(v) }); }
      }, S);
      if (marketUnits === 'lb') unitsToggle(node);
      label = goButton(node, fill(S.goShoe, { v: text(usual) }), function () {
        commit({ shoe: draft.shoe, shoeSys: draft.shoeSys, shoeSkip: null });
        next();
      });
      /* On clothing she may skip it; asked once, then her profile keeps it. */
      if (!shoe && !editing) {
        var skip = button('mc__none', S.skip);
        skip.addEventListener('click', function () {
          commit({ shoeSkip: 1 });
          next();
        });
        node.appendChild(skip);
      }
    }

    /* ---- the advice on a product ---- */
    function sizeInput(size) {
      var wanted = String(size).trim().toLowerCase();
      var inputs = document.querySelectorAll('input[data-option-index="' + data.sizeIndex + '"]');
      for (var i = 0; i < inputs.length; i++) {
        if (String(inputs[i].value).trim().toLowerCase() === wanted && !inputs[i].closest('[data-option-mirror]')) return inputs[i];
      }
      var select = document.querySelector('select[data-option-index="' + data.sizeIndex + '"]');
      return select || null;
    }

    function currentColour() {
      if (!(data.colorIndex >= 0)) return null;
      var c = document.querySelector('input[data-option-index="' + data.colorIndex + '"]:checked') || document.querySelector('select[data-option-index="' + data.colorIndex + '"]');
      return c ? c.value : null;
    }

    function stock(size) {
      var v = data.variants || [];
      var colour = currentColour();
      var here = null;
      var colours = [];
      for (var i = 0; i < v.length; i++) {
        if (v[i].o[data.sizeIndex] !== size) continue;
        var c = data.colorIndex >= 0 ? v[i].o[data.colorIndex] : null;
        if (c === colour || c === null) here = here || v[i].a;
        if (c !== null && v[i].a && colours.indexOf(c) === -1) colours.push(c);
      }
      return { here: here !== false, colours: colours };
    }

    function usualText(res) {
      var u = res.given || { size: res.usual, sys: res.sys };
      return (u.sys === 'us' ? 'US ' : '') + fmt(u.size, 1, sep);
    }

    function reasonLine(res) {
      if (shoe) {
        if (res.basis === 'foot') return fill(S.shoeFoot, { foot: fmt(res.foot, 1, sep), size: res.pick, col: fmt(res.col, 1, sep) });
        if (res.basis === 'footStd') return fill(S.shoeFootStd, { foot: fmt(res.foot, 1, sep), size: res.pick });
        if (res.runs === 'small') return fill(S.shoeSmall, { size: res.pick, usual: usualText(res) });
        if (res.half) return fill(S.shoeHalf, { usual: usualText(res), size: res.pick });
        if (res.range) return fill(S.shoeRange, { size: res.pick, usual: usualText(res) });
        if (res.runs === 'large') return fill(S.shoeLarge, { size: res.pick });
        return fill(a.shoe.footOk ? S.shoeUsual : S.shoeUsualOnly, { size: res.pick });
      }
      if (res.basis === 'standard') return fill(S.standard, { size: res.pick });
      return '';
    }

    /* The advice in the name of an adviser (28-10-2026): a portrait (snippets/maatcoach.liquid, data-mc-advisor) beside the
       label store.maatcoach.advisor_label, in place of the plain label, so no line is added. Without the portrait the plain
       label; Verellia shows no adviser (ICP.md), so the plain label is the default. */
    function adviceLabel() {
      var portrait = box.getAttribute('data-mc-advisor');
      if (!portrait || !S.advisorLabel) return el('p', 'mc__label', S.label);
      var p = el('p', 'mc__label mc__label--advisor');
      var img = el('img', 'mc__face');
      img.src = portrait;
      img.alt = '';
      img.width = 40;
      img.height = 40;
      p.appendChild(img);
      p.appendChild(el('span', '', S.advisorLabel));
      return p;
    }

    /* Under the advice, that her answers are kept for the other models, with the link to change them on the same line (owner
       28-10-2026, same mock-up). Only when they really were kept: in a private window nothing stays, and then only the link. */
    function savedLine(node) {
      if (!S.saved || !load(KEY)) { editLink(node); return; }
      var p = el('p', 'mc__saved');
      p.appendChild(icon('check', 'mc__saved-icon'));
      p.appendChild(el('span', '', S.saved));
      editLink(p, S.editShort);
      node.appendChild(p);
    }

    /* One short line under the size, or none (less is more): how it relates to her usual size. */
    function lineFor(res) {
      if (shoe || res.basis === 'standard') return reasonLine(res);
      var u = String(profile.u || '');
      if (u && MC.letterIndex(u) > -1 && MC.letterIndex(res.pick) > -1) {
        return MC.letterIndex(u) === MC.letterIndex(res.pick) ? fill(S.usualSame, { size: res.pick }) : fill(S.usualDiff, { usual: u, size: res.pick });
      }
      return '';
    }

    /* Fase 2 (27-10-2026): she may have chosen a size before this script ran (it loads last; on 3G that is seconds after the
       buttons show). A size that is checked but was not checked in the HTML is her choice, so the advice never replaces it. */
    var picked = /[?&]variant=\d/.test(window.location.search) || Array.prototype.some.call(
      document.querySelectorAll('input[data-option-index="' + data.sizeIndex + '"]'),
      function (i) { return i.checked && !i.defaultChecked; });
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (event.isTrusted && t && t.matches && t.matches('input[data-option-index="' + data.sizeIndex + '"]')) picked = true;
    });
    function autoPick(res) {
      if (picked || !res || res.status !== 'ok' || !res.pick) return;
      var input = sizeInput(res.pick);
      if (!input || input.type !== 'radio' || input.checked || !stock(res.pick).here) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function choose(size) {
      var input = sizeInput(size);
      if (!input) return false;
      if (input.type === 'radio') {
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        input.value = size;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      picked = true;
      return true;
    }

    /* "Ajouter le M au panier": her size chosen on the page, the pop-up closed, and the page's own add button pressed, so
       the bag, the drawer and the tracking work exactly as with a tap on that button. */
    function addToBag(size) {
      if (!choose(size)) return;
      var close = dialog && dialog.querySelector('[data-size-guide-close]');
      if (close) close.click();
      else if (dialog && dialog.open && dialog.close) dialog.close();
      setTimeout(function () {
        var add = document.querySelector('[data-product-form] [data-add-button]');
        if (add && !add.disabled) add.click();
      }, 160);
    }

    function zoneWord(z) {
      var c = z.z;
      if (z.zone === 'length') return c <= -0.45 ? S.fwShort : c >= 0.45 ? S.fwLong : S.fwMid;
      return c <= -0.45 ? S.fwNear : c >= 0.45 ? S.fwLoose : S.fwMid;
    }

    function stepResult(node) {
      var res = MC.advise(a, profile);
      if (res.status === 'need' || res.status === 'none') { show(steps[0], 0); return; }
      if (res.status === 'out') {
        var p = el('p', 'mc__note', fill(shoe ? S.outShoe : S.out, { size: res.pick }) + ' ');
        var link = el('a', '', shoe ? S.contactShoe : S.contact);
        link.href = S.contactUrl || '/pages/contact';
        p.appendChild(link);
        node.appendChild(p);
        editLink(node);
        return;
      }
      node.appendChild(adviceLabel());
      var size = String(res.pick);
      var ring = el('p', 'mc__ring' + (size.length > 3 ? ' mc__ring--long' : ''));
      ring.appendChild(el('span', 'mc__ring-size', size));
      node.appendChild(ring);
      var line = lineFor(res);
      if (line) node.appendChild(el('p', 'mc__line', line));
      if (res.flags && res.flags.roomy) node.appendChild(el('p', 'mc__note', fill(shoe ? S.roomyShoe : S.roomy, { size: res.pick })));
      if (res.flags && res.flags.snug) node.appendChild(el('p', 'mc__note', fill(shoe ? S.snugShoe : S.snug, { size: res.pick })));

      /* The fit per zone (owner 27-10-2026: "pasvorm per zone"): tap a size and see how it would sit, as a dot and a
         word per zone, no centimetres. */
      var sizes = shoe ? [] : a.sizes.filter(function (s) { return MC.zones(a, profile, s).length; });
      var action = el('div', 'mc__action');
      if (sizes.length >= 2) {
        var fit = el('div', 'mc-fit');
        var chips = el('div', 'mc-fit__sizes');
        chips.setAttribute('role', 'group');
        var rows = el('div', 'mc-fit__rows');
        sizes.forEach(function (s) {
          var b = button('mc-fit__size' + (s === res.pick ? ' is-advised' : ''), s);
          b.setAttribute('aria-pressed', 'false');
          b.addEventListener('click', function () { paint(s); });
          chips.appendChild(b);
        });
        fit.appendChild(chips);
        fit.appendChild(rows);
        node.appendChild(fit);
        var paint = function (s) {
          var all = chips.querySelectorAll('button');
          for (var i = 0; i < all.length; i++) all[i].setAttribute('aria-pressed', all[i].textContent === s ? 'true' : 'false');
          rows.innerHTML = '';
          MC.zones(a, profile, s).forEach(function (z) {
            var r = el('div', 'mc-fit__row');
            var name = z.zone === 'chest' && men ? S.fzChestMen : S['fz' + z.zone.charAt(0).toUpperCase() + z.zone.slice(1)];
            r.appendChild(el('span', 'mc-fit__zone', name));
            var tr = el('span', 'mc-fit__track');
            var dot = el('span', 'mc-fit__dot');
            dot.style.left = (50 + Math.max(-1.25, Math.min(1.25, z.z)) / 1.25 * 44) + '%';
            tr.appendChild(dot);
            r.appendChild(tr);
            r.appendChild(el('span', 'mc-fit__word', zoneWord(z)));
            rows.appendChild(r);
          });
          renderAction(s);
        };
        node.appendChild(action);
        paint(size);
      } else {
        node.appendChild(action);
        renderAction(size);
        /* Without a fit per zone (shoes, a model without measurements): the other size, when there is one, one tap away. */
        if (res.alt && sizeInput(res.alt) && stock(res.alt).here) {
          var alt = button('mc__alt', fill(S.or, { size: res.alt }));
          alt.addEventListener('click', function () { renderAction(res.alt); });
          node.appendChild(alt);
        }
      }
      function renderAction(s) {
        action.innerHTML = '';
        var st = stock(s);
        if (!st.here) {
          var others = st.colours.filter(function (c) { return c !== currentColour(); });
          action.appendChild(el('p', 'mc__note', fill(others.length ? S.soldHere : S.soldAll, { size: s, colours: others.join(', ') })));
          return;
        }
        if (!sizeInput(s)) return;
        var add = button('btn btn--full mc__add', fill(S.add, { size: s }));
        add.addEventListener('click', function () { addToBag(s); });
        action.appendChild(add);
        /* Short (owner 28-10-2026): the days she has, without "if the size does not fit" right under the button. */
        var reassure = S.reassureShort || (shoe ? S.reassureShoe : S.reassure);
        if (reassure) action.appendChild(el('p', 'mc__reassure', reassure));
      }
      savedLine(node);
    }

    /* ---- her sizes for an outfit (the looks page): every piece with the size its own chart gives her, the way its product
       page would advise it. The sizes are already chosen in the outfit behind the pop-up (assets/looks.js); a size sold out
       in the colour of the look says so. ---- */
    function stepLook(node) {
      var pieces = box.mcLook ? box.mcLook.pieces : [];
      node.appendChild(el('p', 'mc__label', S.lookLabel));
      var list = el('ul', 'mc-look');
      pieces.forEach(function (piece) {
        var res = piece.data ? MC.advise(MC.analyse(piece.data), profile) : null;
        var pick = res && res.status === 'ok' && res.pick != null ? String(res.pick) : '';
        var row = el('li', 'mc-look__row');
        row.appendChild(el('span', 'mc-look__name', piece.name));
        var size = el('span', 'mc-look__size' + (pick ? '' : ' is-empty'), pick || S.lookNone);
        if (pick && piece.avail && piece.avail.indexOf(pick) === -1) size.appendChild(el('span', 'mc-look__sold', S.hintSold));
        row.appendChild(size);
        list.appendChild(row);
      });
      node.appendChild(list);
      var go = button('btn btn--full mc__add', S.lookGo);
      go.addEventListener('click', function () { if (dialog && dialog.open) dialog.close(); });
      node.appendChild(go);
      editLink(node);
    }

    /* "Change my answers": the profile, where each answer has its own pencil. */
    function editLink(node, text) {
      var edit = button('mc__edit', text || S.edit);
      edit.addEventListener('click', function () {
        if (!meBtn) {
          startDraft();
          steps = stepList();
          show(steps[0], -1);
          return;
        }
        from = at;
        show('me', 1);
      });
      node.appendChild(edit);
    }

    /* ---- the profile (owner 27-10-2026: "een profiel icon ... dat ze al hun gegevens kunnen invullen ... opgeslagen op
       basis van apparaat id"): her silhouette and her name, the profiles on this device, every
       answer with a pencil that opens that one question, the device id, and a button that erases it all. ---- */
    var ZONE_KEY_WORD = { zc: 'Chest', zw: 'Waist', zh: 'Hip' };
    function answer(k) {
      if (k === 'n') { var me = readMe(); return me.naam ? fill(S.pNameIs, { name: me.naam }) : ''; }
      if (k === 'age') { var a = readMe().age; return a ? fill(S.goAge, { v: a }) : ''; }
      if (k === 'h') return profile.h ? fill(S.goHeight, { v: heightText(profile.h) }) : '';
      if (k === 'w') return profile.w ? fill(S.goWeight, { v: weightText(profile.w) }) : '';
      if (k === 'u') return profile.u ? String(profile.u) : '';
      if (ZONE_KEY_WORD[k]) {
        var o = profile[k];
        if (!o) return '';
        var key = k === 'zc' && men ? 'ChestMen' : ZONE_KEY_WORD[k];
        return S['o' + key + o.charAt(0).toUpperCase() + o.slice(1)] || '';
      }
      if (k === 'f') return { near: S.fitNear, regular: S.fitRegular, loose: S.fitLoose }[profile.f] || '';
      if (k === 'shoe') return profile.shoe ? fill(S.goShoe, { v: (profile.shoeSys === 'us' ? 'US ' : '') + fmt(profile.shoe, 1, profile.shoeSys === 'us' ? '.' : sep) }) : '';
      return '';
    }

    function stepMe(node) {
      var d = readDevice();
      keep(DEVICE, d);
      var me = readMe();
      var top = el('div', 'mc-me__head');
      var av = el('span', 'mc-me__avatar');
      av.setAttribute('aria-hidden', 'true');
      if (!men && profile.h && profile.w) av.innerHTML = avatar(profile);
      else av.innerHTML = ICONS.user;
      top.appendChild(av);
      top.appendChild(el('h3', 'mc-me__name', me.naam || S.pNone));
      node.appendChild(top);

      var rows = [
        ['n', S.pName], ['age', S.pAge], ['h', S.pHeight], ['w', S.pWeight], ['u', S.pUsual],
        ['zc', men ? S.fzChestMen : S.fzChest], ['zw', S.fzWaist], ['zh', S.fzHip], ['f', S.pFit], ['shoe', S.pShoe]
      ];
      var list = el('div', 'mc-me__list');
      rows.forEach(function (r) {
        var value = answer(r[0]);
        var row = button('mc-me__row', '');
        row.appendChild(el('span', 'mc-me__key', r[1]));
        var val = el('span', 'mc-me__val' + (value ? '' : ' is-empty'), value || S.pEmpty);
        val.appendChild(icon('pen', 'mc-me__pen'));
        row.appendChild(val);
        row.setAttribute('aria-label', r[1] + ' : ' + (value || S.pEmpty) + '. ' + S.hintEdit);
        row.addEventListener('click', function () {
          startDraft();
          editing = true;
          show(r[0], 1);
        });
        list.appendChild(row);
      });
      node.appendChild(list);

      var foot = el('div', 'mc-me__foot');
      foot.appendChild(el('p', 'mc-me__device', fill(S.pDevice, { id: d.id })));
      foot.appendChild(el('p', 'mc-me__note', S.privacy));
      var erase = button('mc-me__erase', '');
      erase.appendChild(icon('trash', 'mc-me__erase-icon'));
      erase.appendChild(el('span', '', S.pErase));
      erase.addEventListener('click', function () {
        eraseAll();
        profile = {};
        startDraft();
        editing = false;
        from = null;
        steps = stepList();
        show(steps[0], -1);
      });
      foot.appendChild(erase);
      if (S.privacyUrl) {
        var pol = el('a', 'mc-me__policy', S.pPrivacy);
        pol.href = S.privacyUrl;
        foot.appendChild(pol);
      }
      node.appendChild(foot);
    }

    /* The age, 16 to 99, kept where the menu keeps it (verellia-profil, snippets/menu-profil.liquid), so both show
       the same. It does not change the advice; on the way through she may skip it. */
    function stepAge(node) {
      question(node, S.qAge);
      var host = el('div', '');
      node.appendChild(host);
      var age = Number(readMe().age);
      if (!(age >= 16 && age <= 99)) age = 40;
      var label;
      ruler = new Ruler(host, {
        label: S.qAge, min: 16, max: 99, step: 1, tick: 10, value: age,
        major: function (v) { return v % 10 === 0 ? String(v) : null; },
        mid: function (v) { return v % 5 === 0; },
        show: function (v) { return String(v); },
        onInput: function (v) { age = v; label.textContent = fill(S.goAge, { v: v }); }
      }, S);
      label = goButton(node, fill(S.goAge, { v: age }), function () {
        var m = readMe();
        m.age = age;
        saveMe(m);
        keep(DEVICE, readDevice());
        changed();
        next();
      });
      if (!editing) {
        var skip = button('mc__none', S.skip);
        skip.addEventListener('click', next);
        node.appendChild(skip);
      }
    }

    /* The first name, from the profile; the same key the menu greets him with. */
    function stepName(node) {
      question(node, S.pNameQ);
      var input = el('input', 'mc-me__input');
      input.type = 'text';
      input.maxLength = 30;
      input.autocomplete = 'given-name';
      input.value = readMe().naam || '';
      input.setAttribute('aria-label', S.pName);
      node.appendChild(input);
      var save = function () {
        var m = readMe();
        m.naam = input.value.trim();
        saveMe(m);
        keep(DEVICE, readDevice());
        changed();
        next();
      };
      goButton(node, S.pSave, save);
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          save();
        }
      });
      setTimeout(function () {
        try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
      }, 280);
    }

    /* ---- the line next to the sizes and the mark on her size (product page) ---- */
    function renderHint(res) {
      var marked = document.querySelectorAll('.is-advised');
      for (var i = 0; i < marked.length; i++) {
        if (marked[i].closest('.mc')) continue;
        marked[i].classList.remove('is-advised', 'is-advised--ring');
        var sr = marked[i].querySelector('.mc-sr');
        if (sr) sr.parentNode.removeChild(sr);
      }
      if (!hint) return;
      hint.innerHTML = '';
      hint.classList.remove('mc-hint--ask', 'mc-hint--out', 'mc-hint--done');
      var open = el('button', 'mc-hint__open', '');
      open.type = 'button';
      open.setAttribute('data-size-guide-open', '');
      open.setAttribute('aria-haspopup', 'dialog');
      open.setAttribute('aria-controls', 'SizeGuideDialog');
      if (hint.hasAttribute('data-mc-compact')) {
        var advised = res.status !== 'need' && res.status !== 'out';
        hint.classList.add(advised ? 'mc-hint--done' : res.status === 'out' ? 'mc-hint--out' : 'mc-hint--ask');
        if (advice) {
          /* In the head of the sizes, quiet (owner 27-10-2026: "altijd advies maat ... die knop met rondingen ook weghalen ...
             quiet luxury"): "Taille conseillée : M" as a plain underlined line, "Ma taille conseillée" before she answered. */
          if (advised) {
            var parts = fill(shoe ? S.adviceForShoe : S.adviceFor, { size: '\u0000' }).split('\u0000');
            var words = el('span', 'mc-hint__words', parts[0]);
            words.appendChild(el('b', '', String(res.pick)));
            if (parts[1]) words.appendChild(document.createTextNode(parts[1]));
            open.appendChild(words);
          } else {
            open.appendChild(el('span', 'mc-hint__words', res.status === 'out' ? S.hintSee : (shoe ? S.adviceFindShoe : S.adviceFind)));
          }
        } else {
          open.appendChild(icon(advised ? 'check' : 'ruler'));
          open.appendChild(el('span', 'mc-hint__words', advised ? fill(shoe ? S.hintForShoe : S.hintFor, { size: res.pick }) : (hint.getAttribute('data-mc-label') || (shoe ? S.hintFindShoe : S.hintFind))));
        }
        hint.appendChild(open);
        hint.hidden = false;
        if (advised) markAdvised(res.pick);
        return;
      }
      if (res.status === 'need') {
        hint.classList.add('mc-hint--ask');
        hint.appendChild(icon('ruler'));
        var askTitle = el('span', 'mc-hint__ask', shoe ? S.hintFindShoe : S.hintFind);
        askTitle.setAttribute('aria-hidden', 'true');
        hint.appendChild(askTitle);
        var sub = shoe ? (S.hintSubShoe || S.hintSub) : S.hintSub;
        if (sub) hint.appendChild(el('span', 'mc-hint__sub', sub));
        open.textContent = shoe ? S.hintFindShoe : S.hintFind;
        hint.appendChild(open);
        hint.hidden = false;
        return;
      }
      if (res.status === 'out') {
        hint.classList.add('mc-hint--out');
        hint.appendChild(icon('ruler'));
        hint.appendChild(el('span', 'mc-hint__ask', shoe ? S.hintOutShoe : S.hintOut));
        open.textContent = S.hintSee;
        hint.appendChild(open);
        hint.hidden = false;
        return;
      }
      var st = stock(res.pick);
      hint.classList.add('mc-hint--done');
      hint.appendChild(icon('check'));
      hint.appendChild(el('strong', 'mc-hint__size', fill(shoe ? S.hintForShoe : S.hintFor, { size: res.pick })));
      if (!st.here) hint.appendChild(el('span', 'mc-hint__sold', S.hintSold));
      open.textContent = S.hintEdit;
      hint.appendChild(open);
      hint.hidden = false;
      markAdvised(res.pick);
    }

    function markAdvised(pick) {
      var input = sizeInput(pick);
      if (input && input.type === 'radio') {
        var label = document.querySelector('label[for="' + input.id + '"]');
        if (label) {
          label.classList.add('is-advised');
          /* With the quiet line the advised size gets a frame instead of the dot, which read as an accent on the letter. */
          if (advice) label.classList.add('is-advised--ring');
          label.appendChild(el('span', 'mc-sr visually-hidden', ' (' + S.advised + ')'));
        }
      }
    }

    function refresh() {
      var res = MC.advise(a, profile);
      renderHint(res);
      return res;
    }

    function answered() {
      if (look) return !stepList().length;
      var res = MC.advise(a, profile);
      return res.status === 'ok' || res.status === 'out';
    }

    /* Every time the pop-up opens: her advice when she answered before, else the first question. */
    function opened() {
      decideUnits();
      profile = readProfile();
      startDraft();
      editing = false;
      from = null;
      steps = stepList();
      show(answered() ? end : steps[0], 0);
    }

    if (dialog) {
      new MutationObserver(function () { if (dialog.hasAttribute('open')) opened(); }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }

    document.addEventListener('maatcoach:change', function () {
      profile = readProfile();
      if (!look) autoPick(refresh());
    });

    startDraft();
    /* The outfit's pop-up has its own cross (snippets/maatcoach.liquid, context look) and closes on the backdrop too; the
       rest of this function is about the sizes of a product page. */
    if (look) {
      var cross = dialog && dialog.querySelector('[data-mc-look-close]');
      if (cross) cross.addEventListener('click', function () { dialog.close(); });
      if (dialog) dialog.addEventListener('click', function (event) { if (event.target === dialog) dialog.close(); });
      return;
    }
    autoPick(refresh());
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (t && t.matches && t.matches('[data-option-index="' + data.colorIndex + '"]')) autoPick(refresh());
    });
  }

  function start() {
    var boxes = document.querySelectorAll('[data-mc]');
    for (var i = 0; i < boxes.length; i++) new Coach(boxes[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
